import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "@/hooks/useDeskNavigate";
import { guardedSave, useLeaveGuard } from "@/lib/leaveRegistry";
import useGoBack from "@/hooks/useGoBack";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, FileText, MapPin, User, Clock, CheckCircle2, CircleCheck, Circle, AlertTriangle,
  Upload, Download, History, Pencil, RefreshCw, Archive, Info, PauseCircle, PlayCircle, Trash2, RotateCcw,
  Plus, Check, Sparkles, Printer, Eye, Calendar as CalendarIcon, Paperclip, ChevronDown, ChevronUp,
  LockIcon as LockIcon, XCircle,
} from "lucide-react";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import { markSeen } from "@/lib/seen";
import { CloseReopenDialog, AddedChip } from "@/components/documents/DossierParts";
import DossierFieldDialog from "@/components/documents/DossierFieldDialog";
import DeskConfirm from "@/components/crm/DeskConfirm";
import { Pencil as DfPencil, Check as DfCheck, Plus as DfPlus, Trash2 as DfTrash } from "lucide-react";
import DossierPieces from "@/components/documents/DossierPieces";
import { FileActionChip, fileTypeMeta } from "@/components/documents/FileActionChip";
import { LockOpen as DossierLockOpenIcon, Lock } from "lucide-react";
import { OutlookTabs } from "@/components/common/OutlookTabs";
import { API_BASE as _V3_API_BASE } from "@/lib/api";
import { toastRappelsReactivated } from "@/lib/rappelsToast";
import ReopenReasonModal from "@/components/shared/ReopenReasonModal";
import { useAuth } from "@/context/AuthContext";
import { hasRight } from "@/components/crm/crmShared";
import { usePermissions } from "@/context/PermissionsContext";
import { useFilePreview } from "@/context/FilePreviewContext";
import DocumentStatusBadge, { DOC_STATUS_MAP } from "@/components/documents/DocumentStatusBadge";
import { MoneyInput, formatMoney } from "@/components/documents/DocumentCreateDialog";
import DocumentEditDialog from "@/components/documents/DocumentEditDialog";
import TaskCreateDialog from "@/components/tasks/TaskCreateDialog";
import LinkedTaskBadge from "@/components/documents/LinkedTaskBadge";
import HeaderProfileGroup from "@/components/TopBar";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import UploadingOverlay from "@/components/common/UploadingOverlay";
import UploadedFileThumbnail from "@/components/common/UploadedFileThumbnail";
import AutocompleteTextInput from "@/components/common/AutocompleteTextInput";
import UserAvatar from "@/components/ui/UserAvatar";
import { HISTORY_COLORS } from "@/components/ui/HistoryTimeline";
import UnifiedHistoryPanel from "@/components/common/HistoryPanel";
import DeskTip from "@/components/common/DeskTip";
import { useRequiredFields, requiredClass } from "@/lib/useRequiredFields";
import { FieldError } from "@/components/common/RequiredField";
import useUploadQueue from "@/hooks/useUploadQueue";
import FileOrScanInput from "@/components/common/FileOrScanInput";
import ConfirmDeleteFileDialog from "@/components/common/ConfirmDeleteFileDialog";
import DeleteRequiredFileDialog from "@/components/documents/DeleteRequiredFileDialog";
import "@/components/crm/lotDetails.css";
import { DSelect } from "@/components/common/DeskSelect";

const ACCENT = "#2B579A";

/**
 * FileDropTarget — wrapper qui accepte le drag & drop de fichiers.
 * Spec 2026-07-19 (bug 5 correctifs #4). Encapsule ses enfants dans une
 * zone qui écoute `dragover` / `drop` et surligne visuellement l'état
 * "en survol". Un seul fichier est pris en compte (le premier). Le clic
 * classique sur le label enfant (input file caché) reste opérant.
 *
 * Spec 2026-02-XX rev46 (Point 1) — supporte un prop `uploading` qui
 * monte un overlay spinner + libellé pendant le transfert. Bloque
 * visuellement les nouveaux drops (le drop-handler sort tôt via garde).
 */
function FileDropTarget({ onFilePicked, children, className = "", uploading = false, uploadingInfo = null, accent = "#2B579A", ...rest }) {
  const [hover, setHover] = useState(false);
  const on = (e) => { e.preventDefault(); e.stopPropagation(); };
  return (
    <div
      onDragEnter={(e) => { if (uploading) return; on(e); setHover(true); }}
      onDragOver={(e)  => { if (uploading) return; on(e); setHover(true); }}
      onDragLeave={(e) => { on(e); setHover(false); }}
      onDrop={(e) => {
        on(e); setHover(false);
        if (uploading) return;
        const f = e.dataTransfer?.files?.[0];
        if (f) onFilePicked(f);
      }}
      className={`${className} relative`}
      style={hover && !uploading ? { outline: "2px dashed #0078D4", outlineOffset: 2, borderRadius: 6 } : undefined}
      {...rest}
    >
      {children}
      <UploadingOverlay
        uploading={uploading}
        filename={uploadingInfo?.filename}
        progress={uploadingInfo?.progress}
        accent={accent}
      />
    </div>
  );
}

const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  // Spec iter211 (2026-02-28) — GARDE anti-« 01 janv. 01 » : rejette
  // les valeurs qui parsent en Invalid Date OU qui tombent hors d'une
  // plage de sanité (1970 → 2100). Sans cette garde, une chaîne
  // résiduelle non-datante (ex. URL de fichier orphelin après suppression,
  // reliquat legacy scalar mirror) parvenait à ``new Date()`` qui la
  // remontait à un timestamp aberrant (01 janv. 01, 01 janv. 70) au
  // lieu de rester vide. Bug reporté par l'utilisateur : après
  // suppression d'un fichier obligatoire, le champ Date associé
  // affichait « 01 janv. 01 » au lieu de rester vide.
  if (isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  if (y < 1970 || y > 2100) return null;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" });
};
const fmtDateTime = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  if (y < 1970 || y > 2100) return null;
  return d.toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" });};

/**
 * ActionAttachmentsBlock — Spec iter208 (2026-02-28).
 * Rend une liste compacte des justificatifs uploadés lors de l'action
 * Suspendre / Annuler (dropzone modale).
 *
 * Spec iter249 (2026-03-01) — Refonte pour uniformiser avec l'affichage
 * des fichiers d'étape (``FileActionChip``) : icône de type colorée
 * (rouge PDF, bleu DOC, vert XLS…), nom réel du fichier, boutons Voir
 * (œil) + Télécharger (flèche). Auparavant, la bannière « Document
 * annulé » affichait les justificatifs sous forme de pastille trombone
 * cliquable et l'accessibilité au fichier passait par le nom + un
 * survol — inutilisable quand plusieurs justificatifs sont joints.
 */
function ActionAttachmentsBlock({ docId, action, files, accent = "#5A9EE0" }) {
  const { openAuthenticatedUrl, buildDownloadHandler } = useFilePreview();
  if (!Array.isArray(files) || files.length === 0) return null;
  const items = files
    .map((f, idx) => ({
      filename: f.original_filename || "fichier",
      url: `/documents/${docId}/action-attach?action=${action}&idx=${idx}`,
      key: `${action}-${idx}`,
    }));
  return (
    <div className="mt-2" data-testid={`doc-action-attach-list-${action}`}>
      <div className="text-[10px] uppercase tracking-widest mb-1.5" style={{ color: accent }}>
        Justificatifs ({files.length})
      </div>
      <FileChipsGroup
        files={items}
        onView={(f) => openAuthenticatedUrl(f.url, { filename: f.filename })}
        onDownload={(f) => buildDownloadHandler(f.url, { filename: f.filename })()}
        testid={`doc-action-attach-${action}`}
      />
    </div>
  );
}

/**
 * ProgressDonut — spec rev128 (2026-02-05). Donut SVG (56px) affichant
 * la progression du workflow avec un dégradé vert (#4ADE80 → #16A34A).
 * Le pourcentage est centré en gras. Utilisé dans le header du bloc
 * Workflow d'un document AVEC étapes.
 *
 * Spec iter95 (Feb 2026) — variante adaptative :
 *   • ``mode="header"`` (44 px par défaut) : couleur selon avancement
 *     (vert 100 % · orange en cours · gris 0 %), icône Check si 100 %,
 *     « % » affiché en exposant plus petit + semi-transparent.
 *   • ``mode="legacy"`` (défaut historique) : dégradé vert, "%" pleine
 *     taille. Conservé pour les autres usages (SimpleDocumentBlock,
 *     KPIs, etc.) — inchangé côté rendu.
 */
function ProgressDonut({ percent = 0, size = 64, thickness = 6, mode = "legacy", statusColor = null }) {
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  const dash = (percent / 100) * c;
  const gradId = `donut-grad-${size}-${thickness}-${mode}`;

  // Spec iter125 pt.1 (Feb 2026) — mode "header" : anneau SVG plein,
  // background ring #2a2a2e, progression = ``statusColor`` (couleur du
  // statut doc), pourcentage centré en blanc gras. Plus de dégradé, plus
  // d'exposant, plus d'icône check. Mode "legacy" inchangé pour les
  // autres usages (SimpleDocumentBlock, KPIs, listes…).
  const headerStroke = statusColor || "#F97316";
  const useHeader = mode === "header";

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} data-testid="doc-workflow-donut">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#4ADE80" />
            <stop offset="100%" stopColor="#16A34A" />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2} cy={size / 2} r={r}
          stroke={useHeader ? "#2a2a2e" : "rgba(255,255,255,0.08)"}
          strokeWidth={thickness}
          fill="none"
        />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          stroke={useHeader ? headerStroke : `url(#${gradId})`}
          strokeWidth={thickness}
          strokeLinecap="round"
          fill="none"
          strokeDasharray={`${dash} ${c - dash}`}
          strokeDashoffset={c / 4}
          transform={`rotate(-90 ${size / 2} ${size / 2}) scale(-1 1) translate(-${size} 0)`}
          style={{ transition: "stroke-dasharray 0.5s ease-out" }}
        />
      </svg>
      <div
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
        style={{ lineHeight: 1 }}
      >
        {useHeader ? (
          <span
            style={{
              fontWeight: 700,
              color: "#FFFFFF",
              fontSize: Math.round(size * 0.32),
              letterSpacing: "-0.02em",
            }}
          >
            {percent}
            <span style={{ fontSize: Math.round(size * 0.20), marginLeft: 1 }}>%</span>
          </span>
        ) : (
          <span
            style={{
              fontWeight: 700,
              color: "#EDEDED",
              letterSpacing: "-0.02em",
            }}
          >
            <span style={{ fontSize: Math.round(size * 0.30) }}>{percent}</span>
            <span
              style={{
                fontSize: Math.round(size * 0.22),
                marginLeft: 1,
                verticalAlign: "super",
                lineHeight: 1,
              }}
            >%</span>
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * SegmentedProgressBar — spec rev140 (2026-02-06). Barre horizontale
 * composée d'un segment par étape :
 *   • closed        → dégradé vert #22C55E → #16A34A
 *   • current       → gris neutre #4A4A4A (plus aucun doré)
 *   • current late  → dégradé rouge (isCurrentLate)
 *   • future        → gris #2A2A2A avec bordure #3A3A3A
 * Chaque segment porte le libellé de l'étape en tooltip (title).
 */
function SegmentedProgressBar({ steps = [], currentIndex = -1, currentLate = false }) {
  if (!steps.length) return null;
  return (
    <div className="flex items-stretch gap-1 w-full h-2 rounded-full overflow-hidden" data-testid="doc-workflow-segments">
      {steps.map((s, i) => {
        const isDeletedSeg = !!s.deleted_at;
        const isClosed = !!s.closed_at && !isDeletedSeg;
        const isCurrent = !isClosed && !isDeletedSeg && i === currentIndex;
        // Spec iter218 (2026-02-28) — segment tombstoné : rayures diag
        // grises pour marquer l'emplacement historique.
        const bg = isDeletedSeg
          ? "repeating-linear-gradient(45deg, #3F3F46 0 3px, #2A2A2A 3px 6px)"
          : isClosed
          ? "linear-gradient(90deg, #22C55E 0%, #16A34A 100%)"
          : isCurrent
          ? (currentLate
              ? "linear-gradient(90deg, #FCA5A5 0%, #EF4444 100%)"
              : "#4A4A4A")
          : "#2A2A2A";
        return (
          <div
            key={s.id || i}
            className="flex-1 rounded-full"
            style={{
              background: bg,
              border: isDeletedSeg
                ? "1px solid rgba(156,163,175,0.25)"
                : (!isClosed && !isCurrent ? "1px solid #3A3A3A" : "none"),
              opacity: isDeletedSeg ? 0.75 : 1,
            }}
            title={`Étape ${i + 1} — ${s.label || `Étape ${i + 1}`}${isDeletedSeg ? " · supprimée" : isClosed ? " · validée" : isCurrent ? (currentLate ? " · en retard" : " · prochaine") : ""}`}
            data-testid={`doc-workflow-segment-${i}`}
          />
        );
      })}
    </div>
  );
}

/**
 * Rosette — spec rev138 (2026-02-06).
 * REFONTE : la rosette dentée (sceau crantée) est remplacée par un CARRÉ
 * ARRONDI (border-radius 8px) en dégradé vert (#4ADE80 → #16A34A) avec
 * le numéro d'étape centré en vert foncé (#052A11). Cohérent avec le
 * badge doré déjà utilisé pour l'étape « en cours » — même famille de
 * forme (rounded-square), couleur différente selon l'état.
 */
function Rosette({ size = 32, color = "#22C55E", children }) {
  return (
    <div
      className="flex items-center justify-center font-bold shrink-0"
      style={{
        width: size,
        height: size,
        borderRadius: Math.round(size * 0.25),
        background: "linear-gradient(135deg, #4ADE80 0%, #16A34A 100%)",
        color: "#052A11",
        border: "1.5px solid rgba(22,163,74,0.55)",
        boxShadow: "0 0 0 4px rgba(34,197,94,0.18), 0 3px 10px rgba(34,197,94,0.35)",
        fontSize: Math.round(size * 0.42),
        lineHeight: 1,
        letterSpacing: "-0.02em",
        fontFamily: "Tahoma, sans-serif",
      }}
    >
      {children}
    </div>
  );
}

/**
 * BoltIcon — spec rev132 (2026-02-05). Petit éclair SVG (inspiré de
 * Tabler `bolt`) rendu au centre d'une rosette pour signaler un
 * document « rempli et complété » sans workflow.
 */
function BoltIcon({ size = 12, color = "#0B1F0F" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
      <path d="M13 3l0 7l6 0l-8 11l0 -7l-6 0l8 -11z" />
    </svg>
  );
}

/**
 * FileActionChip — spec rev128 (2026-02-05). Mini-carte cliquable pour
 * un fichier attaché à une étape. Icône colorée selon l'extension du
 * fichier (rouge pour PDF, bleu pour DOC/DOCX, vert pour XLS, violet
 * pour images, gris pour autre) + nom + séparateur + 3 icônes d'action
 * indépendantes (Voir, Télécharger, Imprimer). L'action « Imprimer »
 * ouvre le fichier dans un nouvel onglet (viewer natif du navigateur).
 */
/**
 * SimpleDocumentBlock — spec rev132 (2026-02-05). Bloc « SUIVI » qui
 * remplace le Workflow pour les documents sans étape
 * (`workflow_mode === "simple"` ou `steps.length === 0`). Reprend
 * exactement la structure d'une carte Workflow :
 *   - En-tête : titre « SUIVI » + « 1 / 1 · Complet » en vert à droite.
 *   - Une seule ligne « étape unique » avec Rosette verte contenant
 *     un éclair (BoltIcon), texte « Document rempli et complété » et
 *     date + auteur à droite.
 *   - Chip fichier optionnelle sous la rosette, comme les vraies étapes.
 */

/**
 * FileChipsGroup — spec rev133 (2026-02-06). Regroupe les chips fichiers
 * attachés à une étape (ou au bloc SUIVI) :
 *   • 0 fichier  → rien.
 *   • 1 fichier  → affichage direct de la chip (rendu inchangé).
 *   • 2+ fichiers → badge cliquable « trombone + N pièces jointes +
 *     chevron ». Au clic, la liste des chips complète est révélée
 *     (icône type + nom + Voir/Télécharger, même style qu'une chip
 *     individuelle mais SANS le bouton Imprimer pour rester compact —
 *     l'action Imprimer reste disponible via le bouton Voir → viewer
 *     natif → Ctrl+P).
 * Accepte un tableau `files = [{ filename, url, key }]`.
 */
function FileChipsGroup({ files = [], onView, onDownload, testid = "file-chips-group" }) {
  const [expanded, setExpanded] = useState(false);
  if (!files || files.length === 0) return null;
  if (files.length === 1) {
    const f = files[0];
    return (
      <div data-testid={`${testid}-single`}>
        <FileActionChip
          filename={f.filename}
          url={f.url}
          onView={() => onView(f)}
          onDownload={() => onDownload(f)}
          testid={`${testid}-chip-single`}
        />
      </div>
    );
  }
  return (
    <div data-testid={testid}>
      {/* Badge groupé cliquable */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="inline-flex items-center gap-2 rounded-lg px-2.5 py-1.5 transition-colors hover:brightness-125"
        style={{
          background: "#1E1E1E",
          border: "1px solid #2A2A2A",
          color: "#EDEDED",
        }}
        data-testid={`${testid}-toggle`}
        aria-expanded={expanded}
      >
        <div
          className="flex items-center justify-center rounded-md shrink-0"
          style={{
            width: 26, height: 26,
            background: "rgba(156,163,175,0.10)",
            border: "1px solid rgba(156,163,175,0.20)",
          }}
        >
          <Paperclip size={13} strokeWidth={2.2} style={{ color: "#9CA3AF" }} />
        </div>
        <span className="text-[12px] font-semibold" style={{ letterSpacing: "0.01em" }}>
          {files.length} pièces jointes
        </span>
        {expanded
          ? <ChevronUp size={13} strokeWidth={2.4} style={{ color: "#9CA3AF" }} />
          : <ChevronDown size={13} strokeWidth={2.4} style={{ color: "#9CA3AF" }} />}
      </button>
      {expanded && (
        <div className="flex flex-col gap-1.5 mt-2 ml-3 pl-3" style={{ borderLeft: "1px dashed #2A2A2A" }}>
          {files.map((f) => (
            <div key={f.key || f.filename}>
              <FileActionChip
                filename={f.filename}
                url={f.url}
                onView={() => onView(f)}
                onDownload={() => onDownload(f)}
                testid={`${testid}-item-${f.key || f.filename}`}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SimpleDocumentBlock({ doc, usersById = {} }) {
  const { openAuthenticatedUrl, buildDownloadHandler } = useFilePreview();
  const validator = doc.terminee_by
    ? usersById[doc.terminee_by]
    : (doc.created_by ? usersById[doc.created_by] : null);
  const iso = doc.terminee_at || doc.updated_at || doc.created_at;
  // Fichiers globaux du document (champs de type "file" au niveau doc,
  // pas au niveau étape). Spec rev158 (2026-02-07) — sources croisées :
  //   • `filenames` legacy (string ou liste par field_id) — anciens docs.
  //   • `values[fieldId]` moderne (dict ou liste de dicts avec
  //     `original_filename` / `storage_path`).
  // Nom de fichier en cascade (comme dans doc-values-card). URL sur
  // l'endpoint réel `/documents/{id}/field-file/{key}?idx=N`.
  const values = doc.values || {};
  const filenames = doc.filenames || doc.field_filenames || {};
  const fileChips = [];
  const _basename = (s) => (typeof s === "string" ? s.split(/[\\/]/).pop() : "");
  const _pickName = (entry, legacy) => {
    if (typeof entry === "string" && entry) return entry;
    if (entry && typeof entry === "object") {
      return entry.original_filename
        || _basename(entry.storage_path)
        || (typeof legacy === "string" ? legacy : "")
        || _basename(entry.url)
        || "fichier";
    }
    if (typeof legacy === "string" && legacy) return legacy;
    return "fichier";
  };
  const _seen = new Set();
  const _push = (fieldId, i, entry, legacy) => {
    const k = `${fieldId}-${i}`;
    if (_seen.has(k)) return;
    _seen.add(k);
    fileChips.push({
      filename: _pickName(entry, legacy),
      url: `/documents/${doc.id}/field-file/${encodeURIComponent(fieldId)}?idx=${i}`,
      idx: i,
      fieldId,
    });
  };
  Object.entries(values).forEach(([fieldId, raw]) => {
    const legacy = filenames[fieldId];
    if (Array.isArray(raw)) {
      raw.forEach((e, i) => {
        if (e) _push(fieldId, i, e, Array.isArray(legacy) ? legacy[i] : legacy);
      });
    } else if (raw && typeof raw === "object") {
      _push(fieldId, 0, raw, legacy);
    }
  });
  Object.entries(filenames).forEach(([fieldId, raw]) => {
    if (!raw) return;
    const list = Array.isArray(raw) ? raw : [raw];
    list.forEach((fn, i) => fn && _push(fieldId, i, fn, fn));
  });

  return (
    <Card
      className="p-5"
      style={{
        background: "linear-gradient(180deg, #1C1C1C 0%, #161616 100%)",
        border: "1px solid #262626",
      }}
      data-testid="doc-simple-block"
    >
      {/* --- Header : SUIVI + 1/1 · Complet ------------------------- */}
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
          Suivi
        </div>
        <div
          className="text-[12px] font-semibold flex items-center gap-1.5"
          style={{ color: "#22C55E" }}
          data-testid="doc-simple-progress"
        >
          <span className="tabular-nums">1 / 1</span>
          <span className="opacity-60">·</span>
          <span>Complet</span>
        </div>
      </div>

      {/* --- Ligne unique « étape » ---------------------------------- */}
      <div
        className="grid grid-cols-[40px_1fr] gap-3"
        data-testid="doc-simple-step"
        style={{ opacity: 0.85 }}
      >
        {/* Rosette avec coche cercle au centre (spec rev143) — remplace
            l'éclair par un cercle vide avec Check dedans, symbole
            universel « complet/validé » plus sobre. */}
        <div className="flex flex-col items-center pt-1">
          <Rosette size={32} color="#22C55E">
            <Check size={16} strokeWidth={3} color="#0B1F0F" />
          </Rosette>
        </div>

        <div className="min-w-0">
          <div
            className="rounded-lg pb-2 pt-2.5 px-3.5"
            style={{ background: "rgba(34,197,94,0.04)", border: "1px solid rgba(34,197,94,0.20)" }}
          >
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-[13.5px]" style={{ color: "#EDEDED" }}>
                Document rempli et complété
              </span>
              <div className="flex-1" />
              <span className="text-[10.5px] font-medium flex items-center gap-1" style={{ color: "#86EFAC" }}>
                <Check size={10} strokeWidth={3} />
                {iso ? fmtDate(iso) : "—"}
                {validator && (
                  <> · {validator.first_name || "—"}</>
                )}
              </span>
            </div>
          </div>
          {/* Chips fichiers sous la carte — regroupées si multi (rev133) */}
          {fileChips.length > 0 && (
            <div className="mt-2">
              <FileChipsGroup
                files={fileChips.map((it) => ({ filename: it.filename, url: it.url, key: `${it.fieldId}-${it.idx}` }))}
                onView={(f) => openAuthenticatedUrl(f.url, { filename: f.filename })}
                onDownload={(f) => buildDownloadHandler(f.url, { filename: f.filename })()}
                testid="doc-simple-files"
              />
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

/**
 * DocumentDetail — vertical timeline of the workflow steps of a single
 * document. Each active step exposes signature / scan / comment /
 * validation actions when the corresponding option is enabled on the
 * DocumentType template.
 */
export default function DocumentDetail({ idOverride = null }) {
  const goBack = useGoBack("/documents");
  // Spec iter307 — `idOverride` : montage en aperçu rapide (QuickViewDialog) hors route.
  const { id: routeId } = useParams();
  const id = idOverride || routeId;
  const navigate = useNavigate();
  const { user } = useAuth();
  const { can: permsCan } = usePermissions();
  const { openAuthenticatedUrl, buildDownloadHandler } = useFilePreview();

  const [doc, setDoc] = useState(null);
  const [type, setType] = useState(null);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("details");      // "details" | "history"
  const [editOpen, setEditOpen] = useState(false);
  // Spec 2026-02-XX rev16 (point J) — dialog création tâche liée + résumé
  // des tâches déjà créées depuis ce document.
  const [linkTaskOpen, setLinkTaskOpen] = useState(false);
  const [linkedTasks, setLinkedTasks] = useState({ counts: {}, items: [], total: 0 });
  // Spec rev148 (2026-02-06) — dialogues Suspendre / Reprendre / Supprimer
  // désormais déclenchés par les boutons de la barre haute (au lieu du
  // bloc « Action administrateur » retiré du bas de fiche).
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState("");
  // Spec iter206 pt.2 (2026-02-28) — Annulation définitive du doc.
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const cancelReq = useRequiredFields([{ key: "cancel-reason", label: "Motif d'annulation", ok: !!cancelReason.trim() }]);
  const suspendReq = useRequiredFields([{ key: "suspend-reason", label: "Motif", ok: !!suspendReason.trim() }]);
  const [cancelSaving, setCancelSaving] = useState(false);
  const [suspendSaving, setSuspendSaving] = useState(false);
  // Spec iter208 (2026-02-28) — Dropzones dans les modales Suspendre /
  // Annuler pour attacher des justificatifs au moment même de l'action.
  // Les fichiers sont uploadés APRÈS le changement de statut (le doc doit
  // être en statut suspendu/annule pour recevoir des action-attachments).
  const suspendFileQueue = useUploadQueue();
  const cancelFileQueue = useUploadQueue();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteSaving, setDeleteSaving] = useState(false);
  // Spec rev149 (2026-02-06) — Modale « Corriger » (bouton globale et par
  // étape). L'ouverture est déclenchée par un event window
  // `doc-corriger-open` (voir VerificationDecisionCard et boutons
  // Corriger par étape).
  const [corrigerOpen, setCorrigerOpen] = useState(false);
  const [corrigerScope, setCorrigerScope] = useState(null); // { scope: "document"|"step", stepIndex?, stepLabel? }
  const [corrigerReason, setCorrigerReason] = useState("");
  const [corrigerSaving, setCorrigerSaving] = useState(false);
  // Spec iter135 (Feb 2026) — modale de réouverture avec motif optionnel
  // pour les documents CLÔTURÉS (bouton « Rouvrir » sur DC détail).
  const [reopenClosedModalOpen, setReopenClosedModalOpen] = useState(false);
  const [reopenClosedBusy, setReopenClosedBusy] = useState(false);
  // iter144 — mode Dossier : dialogues champ / étape à la volée, clôture / ré-ouverture.
  const [dossierDlg, setDossierDlg] = useState(null); // "close" | { kind: "field", field?, value? } | { kind: "delField", field }
  const [fieldsEditMode, setFieldsEditMode] = useState(false); // iter150 P5 — mode modification des champs personnalisés
  const [dossierBusy, setDossierBusy] = useState(false);
  const dossierCall = async (fn, okMsg) => {
    setDossierBusy(true);
    try { const { data } = await fn(); setDoc(data); setDossierDlg(null); if (okMsg) toast.success(okMsg); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setDossierBusy(false); }
  };
  useEffect(() => {
    const handler = (e) => {
      setCorrigerScope(e?.detail || { scope: "document" });
      setCorrigerReason("");
      setCorrigerOpen(true);
    };
    window.addEventListener("doc-corriger-open", handler);
    return () => window.removeEventListener("doc-corriger-open", handler);
  }, []);
  // Spec rev151 (2026-02-06) — Liseré orange temporaire (~2.6s) sur la
  // carte de l'étape qui vient d'être réouverte via /corriger. Permet à
  // l'utilisateur de la localiser immédiatement dans le workflow sans
  // scanner. L'effet s'estompe (fade-out CSS via transition) et se
  // remet à zéro automatiquement.
  const [justCorrectedStep, setJustCorrectedStep] = useState(null);
  const submitCorriger = async () => {
    if (!corrigerReason.trim() || !doc) return;
    setCorrigerSaving(true);
    try {
      // Spec rev150 (2026-02-06) — endpoint dédié `/corriger`. Si scope=step,
      // rouvre l'étape ciblée (closed_at=null, current_step_index recalculé)
      // et repasse le doc en en_cours. Sinon (scope=document), rebascule
      // simplement le status. L'explication est journalisée en historique
      // (event_type=corriger, note=reason).
      const body = { reason: corrigerReason.trim() };
      if (corrigerScope?.scope === "step" && typeof corrigerScope?.stepIndex === "number") {
        body.step_index = corrigerScope.stepIndex;
      }
      if (corrigerScope?.scope === "piece" && corrigerScope?.pieceId) body.piece_id = corrigerScope.pieceId;
      const { data } = await api.post(`/documents/${doc.id}/corriger`, body);
      setDoc(data);
      // Spec rev155 (2026-02-07) — bus d'événements navigateur pour
      // que la liste Documents (chip "Étapes à revalider") se mette à
      // jour en temps réel dès qu'une correction est effectuée, même
      // si la page est déjà montée (retour navigation, deuxième onglet).
      window.dispatchEvent(new CustomEvent("ledesk:doc-mutated", {
        detail: { docId: doc.id, reason: "corriger" },
      }));
      // Cross-tab : un ping localStorage réveille les autres onglets
      // Documents (event "storage"). La valeur (timestamp) n'importe
      // pas — seule la mutation compte.
      try { localStorage.setItem("ledesk:doc-mutated", String(Date.now())); } catch {}
      // Spec rev151 (2026-02-06) — Déclenche le liseré orange sur la
      // carte de l'étape rouverte pour ~2.6s. Puis auto-reset.
      if (corrigerScope?.scope === "step" && typeof corrigerScope?.stepIndex === "number") {
        const target = corrigerScope.stepIndex;
        setJustCorrectedStep(target);
        setTimeout(() => setJustCorrectedStep((cur) => cur === target ? null : cur), 2600);
      }
      toast.success("Correction enregistrée", {
        description: corrigerScope?.scope === "step"
          ? "L'étape a été réouverte et l'explication tracée dans l'historique."
          : corrigerScope?.scope === "piece"
          ? "La demande de correction de la pièce a été tracée et notifiée."
          : "L'explication a été tracée dans l'historique.",
      });
      setCorrigerOpen(false);
      setCorrigerReason("");
      setCorrigerScope(null);
    } catch (err) {
      toast.error("Correction impossible", { description: err.response?.data?.detail || "" });
    } finally {
      setCorrigerSaving(false);
    }
  };

  // Load a field file via the authenticated API and display it in the modal.
  const openFieldFile = (fieldKey, descriptor) => {
    openAuthenticatedUrl(`/documents/${id}/field-file/${fieldKey}`, {
      filename: descriptor?.original_filename || fieldKey,
      contentType: descriptor?.content_type || "",
    });
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/documents/${id}`);
      setDoc(data);
      const [t, u, p, lt] = await Promise.all([
        api.get(`/document-types`).then((r) => r.data.find((x) => x.id === data.type_id)),
        api.get("/users/directory").then((r) => r.data.items || r.data || []),
        api.get("/projects").then((r) => r.data || []).catch(() => []),
        api.get(`/documents/${id}/linked-tasks`).then((r) => r.data).catch(() => ({ counts: {}, items: [], total: 0 })),
      ]);
      setType(t || null);
      setUsers(u);
      setProjects(p);
      setLinkedTasks(lt);
    } catch (err) {
      toast.error("Impossible de charger le document", { description: err.response?.data?.detail || "" });
    } finally {
      setLoading(false);
    }
  };

  // Spec iter156 — ``load`` change à chaque render ; le refetch est
  // volontairement piloté par l'identifiant du document uniquement.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);
  // iter372 pt 1 — ouvert → plus compté dans la barre latérale.
  useEffect(() => { markSeen("documents", id); return () => { markSeen("documents", id); }; }, [id]);

  // Spec rev149 (2026-02-07) — Synchronisation source → notification.
  // Ouvrir la fiche d'un document = « je m'en occupe » → on marque
  // les notifs rattachées à ce document comme lues, elles basculent
  // dans l'onglet Historique du panneau sans intervention manuelle.
  useEffect(() => {
    if (!id) return;
    api.post("/notifications/read-related", { document_id: id }).catch(() => {});
  }, [id]);

  const usersById = useMemo(() => {
    const m = {}; users.forEach((u) => { m[u.id] = u; }); return m;
  }, [users]);

  // État du modal de réactivation (spec iter243) — DÉCLARÉ AVANT tout
  // early return pour respecter les Rules of Hooks.
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [reactivateReason, setReactivateReason] = useState("");
  const [reactivateSaving, setReactivateSaving] = useState(false);
  const reactivateReq = useRequiredFields([{ key: "reactivate-reason", label: "Motif de réactivation", ok: !!reactivateReason.trim() }]);

  if (loading) return <div className="text-sm text-muted-foreground p-6">Chargement…</div>;
  if (!doc) return <div className="text-sm text-muted-foreground p-6">Document introuvable.</div>;

  // iter159 — la matrice fait foi : « Modifier » suffit (créateur/responsable non requis) ; le backend enforce type × utilisateur.
  const canEdit = permsCan("documents", "edit");
  // Spec rev148 (2026-02-06) — Suspendre / Supprimer réservés aux
  // Admin/Premier Admin uniquement. Le bouton d'en-tête ne s'affiche
  // pas pour les rôles inférieurs.
  const isAdmin = !!user && (user.role === "admin" || user.is_first_admin);
  const isSuspended = doc.status === "suspendu";
  const isCloture = doc.status === "cloture";
  const isCancelled = !!doc.cancelled || doc.status === "annule";
  // Spec iter243 (2026-08-31) — VERROU global lecture-seule.
  //   Un document annulé OU clôturé est immuable côté UI : toutes les
  //   actions destructives/mutatives sont masquées ou désactivées.
  //   Consultation, historique, téléchargement des fichiers restent
  //   normaux. Seuls Admin / First Admin gardent une action : réactiver
  //   un doc annulé (bouton dédié — pas de contournement du verrou).
  const isLocked = isCloture || isCancelled;
  // iter144 — mode Dossier.
  const isDossier = (doc.workflow_mode || "") === "dossier";
  const isOuvert = doc.status === "ouvert" || doc.status === "a_corriger";
  const canDossierAct = isAdmin || !!(type?.responsible_user_ids || []).includes(user?.id);
  const canCloseDoc = permsCan("documents", "close");
  const dossierPieces = (doc.pieces || []).filter((p) => !p.deleted_at).length;
  const dossierCounts = { fields: (type?.fields || []).length + (doc.extra_fields || []).length, steps: dossierPieces, done: dossierPieces };

  const submitReactivate = async () => {
    if (!reactivateReq.validate()) return;
    setReactivateSaving(true);
    try {
      const { data } = await api.post(`/documents/${doc.id}/status`, {
        status: "en_cours",
        reason: reactivateReason.trim(),
      });
      setDoc(data);
      setReactivateOpen(false);
      setReactivateReason("");
      toast?.success?.("Document réactivé");
    } catch (e) {
      toast?.error?.(e?.response?.data?.detail || "Réactivation impossible");
    } finally {
      setReactivateSaving(false);
    }
  };

  const submitSuspend = async () => {
    if (!suspendReq.validate()) return;
    setSuspendSaving(true);
    try {
      const { data } = await api.post(`/documents/${doc.id}/status`, {
        status: "suspendu",
        reason: suspendReason.trim(),
      });
      // Spec iter208 — upload des justificatifs (dropzone). Serial pour
      // garder une UX progressive et éviter la saturation. Best-effort :
      // un échec de fichier ne rollback pas la suspension.
      let attachErrors = 0;
      await suspendFileQueue.uploadAll(async (file, { onProgress, signal }) => {
        const form = new FormData();
        form.append("file", file);
        try {
          await api.post(
            `/documents/${doc.id}/action-attach?action=suspend`,
            form,
            {
              headers: { "Content-Type": "multipart/form-data" },
              signal,
              onUploadProgress: (evt) => {
                if (!evt.total) return;
                onProgress(Math.round((evt.loaded / evt.total) * 100), evt.loaded);
              },
            },
          );
        } catch (e) {
          attachErrors += 1;
          throw e;
        }
      });
      const fresh = await api.get(`/documents/${doc.id}`).then((r) => r.data).catch(() => data);
      setDoc(fresh);
      setSuspendOpen(false);
      setSuspendReason("");
      suspendFileQueue.reset();
      toast.success("Document suspendu", {
        description: attachErrors > 0
          ? `Le workflow est gelé. ${attachErrors} justificatif(s) n'ont pas pu être envoyés.`
          : "Le workflow est gelé.",
      });
    } catch (err) {
      toast.error("Impossible de suspendre", { description: err.response?.data?.detail || "" });
    } finally {
      setSuspendSaving(false);
    }
  };

  const submitResume = async () => {
    setSuspendSaving(true);
    try {
      const target = doc.pre_suspend_status || "en_cours";
      const { data } = await api.post(`/documents/${doc.id}/status`, { status: target });
      setDoc(data);
      toast.success("Suspension levée");
    } catch (err) {
      toast.error("Impossible de reprendre", { description: err.response?.data?.detail || "" });
    } finally {
      setSuspendSaving(false);
    }
  };

  const submitDelete = async (reasonArg) => {
    const reason = (reasonArg || "").trim();
    if (!reason) return;
    setDeleteSaving(true);
    try {
      // Le backend accepte le body sur DELETE (pattern déjà utilisé par
      // DocDeleteDialog dans Documents.jsx). ``data`` sur axios.delete = body.
      await api.delete(`/documents/${doc.id}`, { data: { reason } });
      toast.deleted("document", doc.reference, { onUndo: () => api.post(`/documents/${doc.id}/restore`) });
      navigate("/documents");
    } catch (err) {
      toast.error("Suppression impossible", { description: err.response?.data?.detail || "" });
      setDeleteSaving(false);
    }
  };

  // Spec iter82 (Feb 2026) — Refonte partie haute : progression compacte
  // (donut 28 px accolé au titre), boutons + barre profil sur UNE ligne,
  // et nouvelle carte Référence à 4 blocs (RÉFÉRENCE / TYPE / CRÉÉ LE /
  // PAR-avatar-tooltip). La timeline en dessous reste STRICTEMENT
  // inchangée (numérotation, couleurs, icônes, structure).
  const _steps = doc.steps || [];
  const _totalSteps = _steps.length;
  // Spec iter218 (2026-02-28) — Les étapes soft-deleted comptent dans
  // le total (Y du X/Y) mais PAS dans les validées (X). Elles sont
  // rendues en tombstone gris dans la timeline.
  const _closedSteps = _steps.filter((s) => s.closed_at && !s.deleted_at).length;
  const _pctTop = _totalSteps ? Math.round((_closedSteps / _totalSteps) * 100) : 0;
  const _currentIdx = doc.status === "cloture"
    ? -1
    : (() => {
        // 1re étape ni close ni supprimée. Le back met déjà à jour
        // current_step_index en tenant compte des tombstones, mais on
        // recalcule côté client pour survivre à un état legacy/obsolète.
        const idx = _steps.findIndex((s) => !s.closed_at && !s.deleted_at);
        if (idx >= 0) return idx;
        // Aucune étape ouverte : fallback sur current_step_index si
        // fourni, sinon -1 (workflow terminé).
        return typeof doc.current_step_index === "number" ? doc.current_step_index : -1;
      })();
  const _currentStep = _currentIdx >= 0 ? _steps[_currentIdx] : null;
  const _creator = doc.created_by ? usersById[doc.created_by] : null;
  // Spec iter95 (Feb 2026) — Données injectées dans la bande fusionnée
  // « Type · Étapes · Créé le · Débuté le · Dernière activité · Par ».
  //   • Débuté le  = date du 1er ``closed_at`` chronologiquement.
  //   • Dernière activité = ``doc.updated_at`` (fallback ``created_at``).
  const _debuteLe = (() => {
    const closedDates = _steps
      .map((s) => s.closed_at)
      .filter(Boolean)
      .sort();
    return closedDates[0] || null;
  })();
  const _derniereActivite = doc.updated_at || doc.created_at || null;

  return (
    <div className="space-y-4" data-testid="document-detail-page">
      {/* ============================================================
          SPEC ITER82 · Ligne du haut : retour · [actions · sep · profil]
          — `justify-between` avec deux groupes, le groupe droit peut
          revenir à la ligne en priorité (flex-wrap) pour ne jamais
          couper les boutons.
          ============================================================ */}
      <div className="flex items-center gap-3 flex-wrap justify-between">
        <Button variant="ghost" size="sm" onClick={goBack} className="gap-1 shrink-0" data-testid="back-to-docs">
          <ArrowLeft size={14} /> Documents
        </Button>
        <div className="flex items-center gap-2 flex-wrap" data-testid="doc-detail-header-right">
          {isDossier && canDossierAct && !isCancelled && isCloture && (
            <button type="button" onClick={() => window.dispatchEvent(new CustomEvent("doc-corriger-open", { detail: { scope: "document" } }))} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-[12.5px] font-semibold transition-all hover:brightness-125" style={{ background: "#2A1F14", color: "#FB923C", border: "1.5px solid #F97316" }} data-testid="doc-corriger-btn"><AlertTriangle size={14} strokeWidth={2.4} /> À corriger</button>
          )}
          {isDossier && !isCancelled && isOuvert && (
            <button type="button" disabled={!canCloseDoc} title={!canCloseDoc ? "Droit de clôture requis" : "Clôturer le dossier"} data-no-right={!canCloseDoc || undefined} onClick={() => canCloseDoc && setDossierDlg("close")} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-[12.5px] font-semibold transition-all hover:brightness-125 disabled:opacity-50 disabled:cursor-not-allowed" style={{ background: "#1C1A33", color: "#a3a6ec", border: "1.5px solid #5B5FC7" }} data-testid="dossier-close-btn"><LockIcon size={14} strokeWidth={2.4} /> Clôturer</button>
          )}
          {canEdit && !isLocked && (
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-[12.5px] font-semibold transition-all hover:brightness-125"
              style={{ background: "#0F1E2E", color: "#5B9EE8", border: "1.5px solid #0078D4" }}
              data-testid="doc-detail-edit-btn"
              title="Modifier les valeurs du document"
            >
              <Pencil size={14} strokeWidth={2.4} /> Modifier
            </button>
          )}
          {isAdmin && isCloture && !isDossier && (() => {
            const doReopen = async (reason) => {
              if (reopenClosedBusy) return;
              setReopenClosedBusy(true);
              try {
                const payload = reason ? { reason } : {};
                const { data } = await api.post(`/documents/${doc.id}/reopen-closed`, payload);
                setDoc(data);
                toast.success("Document rouvert — étapes conservées");
                // Spec iter134 — confirme la reprise des rappels automatiques.
                toastRappelsReactivated();
                setReopenClosedModalOpen(false);
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Réouverture impossible");
              } finally {
                setReopenClosedBusy(false);
              }
            };
            return (
              <div className="inline-flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setReopenClosedModalOpen(true)}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-[12.5px] font-semibold transition-all hover:brightness-125"
                  style={{
                    background: "rgba(139,92,246,0.15)",
                    color: "var(--status-cloture-docs)",
                    border: "1.5px solid var(--status-cloture-docs)",
                  }}
                  data-testid="doc-detail-reopen-btn"
                  title="Rouvrir le document — étapes conservées"
                >
                  <RotateCcw size={14} strokeWidth={2.4} /> Rouvrir
                </button>
                {/* Spec iter135 (Feb 2026) — modale de motif optionnel. */}
                <ReopenReasonModal
                  open={reopenClosedModalOpen}
                  busy={reopenClosedBusy}
                  entityLabel="ce document"
                  onClose={() => { if (!reopenClosedBusy) setReopenClosedModalOpen(false); }}
                  onConfirm={doReopen}
                />
              </div>
            );
          })()}
          {isAdmin && !isLocked && (
            <button
              type="button"
              onClick={() => isSuspended ? submitResume() : setSuspendOpen(true)}
              disabled={suspendSaving}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-[12.5px] font-semibold transition-all hover:brightness-125 disabled:opacity-60"
              style={{
                background: isSuspended ? "#0F2A1E" : "#2A1F14",
                color: isSuspended ? "#4ADE80" : "#F97316",
                border: `1.5px solid ${isSuspended ? "#4ADE80" : "#F97316"}`,
              }}
              data-testid="doc-detail-suspend-btn"
              title={isSuspended ? "Reprendre le workflow" : "Suspendre le document"}
            >
              {isSuspended ? <PlayCircle size={14} strokeWidth={2.4} /> : <PauseCircle size={14} strokeWidth={2.4} />}
              {isSuspended ? "Reprendre" : "Suspendre"}
            </button>
          )}
          {/* Spec iter206 pt.2 (2026-02-28) — Bouton « Annuler ».
              Admin uniquement, masqué si déjà annulé ou déjà clôturé
              (annulation impossible sur ces états finaux). */}
          {isAdmin && !isLocked && (
            <button
              type="button"
              onClick={() => setCancelOpen(true)}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-[12.5px] font-semibold transition-all hover:brightness-125"
              style={{ background: "#2A1414", color: "#EF4444", border: "1.5px solid #EF4444" }}
              data-testid="doc-detail-cancel-btn"
              title="Annuler définitivement ce document"
            >
              <XCircle size={14} strokeWidth={2.4} /> Annuler
            </button>
          )}
          {/* Spec iter243 (2026-08-31) — Bouton « Réactiver ». Visible
              UNIQUEMENT pour Admin / First Admin sur un document au
              statut annulé. Ouvre un modal de confirmation avec motif
              optionnel avant de repasser le doc à « En cours ». */}
          {isAdmin && isCancelled && (
            <button
              type="button"
              onClick={() => setReactivateOpen(true)}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-[12.5px] font-semibold transition-all hover:brightness-125"
              style={{ background: "#0F2A1E", color: "#4ADE80", border: "1.5px solid #4ADE80" }}
              data-testid="doc-detail-reactivate-btn"
              title="Réactiver le document annulé"
            >
              <PlayCircle size={14} strokeWidth={2.4} /> Réactiver
            </button>
          )}
          {isAdmin && !isLocked && (
            <button
              type="button"
              onClick={() => setDeleteOpen(true)}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-[12.5px] font-semibold transition-all hover:brightness-125"
              style={{ background: "#2A1414", color: "#EF4444", border: "1.5px solid #EF4444" }}
              data-testid="doc-detail-delete-btn"
              title="Supprimer ce document"
            >
              <Trash2 size={14} strokeWidth={2.4} /> Supprimer
            </button>
          )}
          {permsCan("taches", "create") && <button
            type="button"
            onClick={() => setLinkTaskOpen(true)}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-[12.5px] font-semibold transition-all hover:brightness-125"
            style={{ background: "#2A2308", color: "#F9BC26", border: "1.5px solid #F9BC26" }}
            data-testid="doc-detail-create-linked-task-btn"
            title="Créer une tâche liée à ce document"
          >
            <Plus size={14} strokeWidth={2.6} /> Créer une tâche
          </button>}
          <span
            aria-hidden="true"
            data-testid="doc-detail-header-separator"
            style={{ display: "inline-block", width: 2, height: 24, background: "#2A2A2A", margin: "0 4px", borderRadius: 1 }}
          />
          <HeaderProfileGroup />
        </div>
      </div>

      {/* ============================================================
          Spec iter156 (Feb 2026) — Repositionnement en-tête :
            • Cercle SVG à côté du titre (à gauche), agrandi ~56px,
              trait fin 2.5px.
            • Séparateur vertical fin (1px, ~44px de haut) entre le
              cercle et le bloc texte.
            • Sous le titre : pilule violette « Type » (fond #241a38,
              bordure #5B5FC7, texte #a89ff0) + statut (ex. En cours)
              sur la MÊME ligne.
            • L'étape en cours n'est plus affichée dans cette zone
              (visible plus bas dans la timeline et dans la carte
              info « ÉTAPES »).
          ============================================================ */}
      <div className="flex items-start gap-4">
        <div className="shrink-0 pt-1">
          <ProgressDonut
            percent={_pctTop}
            size={56}
            thickness={2.5}
            mode="header"
            statusColor={DOC_STATUS_MAP[doc.status]?.color || "#F97316"}
          />
        </div>
        {/* Séparateur vertical fin */}
        <div
          aria-hidden="true"
          className="shrink-0"
          style={{ width: 1, height: 44, background: "rgba(255,255,255,0.10)", marginTop: 6 }}
          data-testid="doc-header-vertical-sep"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 flex-wrap mb-1">
            <h1 className="text-2xl font-bold leading-tight m-0">{doc.title}</h1>
            {isSuspended && (
              <span
                className="inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-widest px-2 py-0.5 rounded"
                style={{ background: "#2A1F14", color: "#F97316", border: "1px solid rgba(249,115,22,0.35)" }}
                data-testid="doc-header-suspended-badge"
                title={doc.suspend_reason ? `Motif : ${doc.suspend_reason}` : "Document suspendu"}
              >
                <PauseCircle size={11} strokeWidth={2.4} /> Suspendu
              </span>
            )}
            {(linkedTasks?.items || []).map((t) => (
              <LinkedTaskBadge
                key={t.id}
                task={t}
                users={users}
                usersById={usersById}
                onOpen={() => navigate(`/taches/${t.id}`)}
              />
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {type && (
              <span
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold"
                style={{
                  background: "#241a38",
                  border: "1px solid #5B5FC7",
                  color: "#a89ff0",
                }}
                data-testid="doc-header-type"
                title={type.name}
              >
                {type.name}
              </span>
            )}
            <DocumentStatusBadge status={doc.status} cancelled={isCancelled} ring="#1C1C1C"
              actorName={(() => { const u = usersById?.[isCancelled ? (doc.cancelled_by || doc.finalized_by) : doc.finalized_by]; return u ? (`${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email) : ""; })()}
              at={isCancelled ? doc.cancelled_at : (doc.closed_at || doc.finalized_at)} />
            {(isCloture || isCancelled) && (() => {
              // Iter131 — mention de clôture sur la ligne du statut : sép. fin · avatar 22px · nom · date/heure.
              // Iter132 — source : finalized_by/at (posés à la clôture, rétro-remplis côté serveur depuis
              // l'historique) ; repli : dernière activité, sans avatar « ? » ni tiret isolé.
              // iter383 pt 1 — document annulé = clôturé : auteur/date de l'annulation si présents.
              const uid = (isCancelled && doc.cancelled_by) || doc.finalized_by || null;
              const closer = uid && usersById ? usersById[uid] : null;
              const at = (isCancelled && doc.cancelled_at) || doc.finalized_at || doc.activity_at || doc.last_modified_at || null;
              const when = at ? new Date(at).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Africa/Algiers" }) : null;
              if (!closer && !when) return null;
              const name = closer ? (`${closer.first_name || ""} ${closer.last_name || ""}`.trim() || closer.email) : null;
              return (
                <span className="inline-flex items-center" style={{ gap: 8 }} data-testid="doc-closed-by-line">
                  <span aria-hidden="true" style={{ width: 1, height: 16, background: "#3a3a40", display: "inline-block" }} />
                  {closer && (
                    <DeskTip text={name} side="bottom" testId="doc-closed-by-avatar-tip">
                      <span className="inline-flex rounded-full" data-testid="doc-closed-by-avatar"><UserAvatar user={closer} size={22} showPopover={false} variant="initials" /></span>
                    </DeskTip>
                  )}
                  <span style={{ fontSize: 11, color: "#8b8b93" }} data-testid="doc-closed-by-text">{[name, when && (closer ? when : `Dernière activité · ${when}`)].filter(Boolean).join(" · ")}</span>
                </span>
              );
            })()}
          </div>
        </div>
      </div>

      {isDossier && <div style={{ fontSize: 10.5, color: "#71717c", fontFamily: "Tahoma, sans-serif", padding: "0 2px" }} data-testid="dossier-header-meta">{dossierPieces} pièce{dossierPieces > 1 ? "s" : ""} · {isCloture ? `Clôturé le ${fmtDate(doc.closed_at || doc.finalized_at)}` : doc.status === "a_corriger" ? "À corriger" : `Ouvert depuis le ${fmtDate(doc.created_at)}`}</div>}

      {/* Spec iter208 (2026-02-28) — Bandeau ANNULÉ persistant : le doc
          est verrouillé définitivement, on affiche motif + date + les
          justificatifs uploadés dans la modale d'annulation. */}
      {isCancelled && (
        <Card
          className="p-4 border"
          style={{
            background: "linear-gradient(180deg, rgba(239,68,68,0.06), rgba(239,68,68,0.015))",
            borderColor: "rgba(239,68,68,0.35)",
          }}
          data-testid="doc-cancel-banner"
        >
          <div className="flex items-start gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: "rgba(239,68,68,0.18)", color: "#EF4444" }}
            >
              <XCircle size={20} strokeWidth={2.2} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold" style={{ color: "#F87171" }}>
                Document annulé{doc.status === "cloture" ? " · clôturé" : doc.status === "termine" ? " · à vérifier" : ""}
              </div>
              <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                {doc.cancel_reason && (
                  <div><span className="uppercase tracking-widest text-[9px] mr-1.5">Motif</span>{doc.cancel_reason}</div>
                )}
                {doc.cancelled_at && (
                  <div>
                    <span className="uppercase tracking-widest text-[9px] mr-1.5">Le</span>
                    {fmtDateTime(doc.cancelled_at)}
                  </div>
                )}
              </div>
              <ActionAttachmentsBlock
                docId={doc.id}
                action="cancel"
                files={doc.cancel_attachments || []}
                accent="#F87171"
              />
            </div>
          </div>
        </Card>
      )}

      {/* ============================================================
          Bande fusionnée — spec iter95 (Feb 2026).
          Remplace l'ancienne carte Référence 4 blocs + le gros donut
          central qui apportait uniquement le % et « Toutes les étapes
          sont validées ». Les 6 colonnes :
            TYPE · ÉTAPES (X/Y validées) · CRÉÉ LE · DÉBUTÉ LE
            (1re étape validée) · DERNIÈRE ACTIVITÉ · PAR (avatar).
          Les colonnes 2 (ÉTAPES) & 4 (DÉBUTÉ LE) sont masquées et
          repliées quand le doc n'a pas d'étape (mode simple), ce qui
          ramène la bande à 4 colonnes utiles au lieu de laisser des
          « — » inutiles.
          ============================================================ */}
      <Card
        className="bg-card border-vertec-borderDark px-4 py-2.5"
        data-testid="doc-reference-card"
      >
        <div className={`grid ${_totalSteps > 0 ? "grid-cols-6" : "grid-cols-4"} divide-x divide-white/5`}>
          {/* Spec iter125 pt.3 (Feb 2026) — la cellule TYPE devient
              RÉFÉRENCE (le type passe sous le titre). Style tabular
              pour l'alignement de la référence type-ancre. */}
          <div className="px-3" data-testid="doc-reference-ref-cell">
            <div className="text-[9.5px] uppercase tracking-widest text-muted-foreground">Référence</div>
            {/* Spec iter146 (Feb 2026) — point 7 : référence en pilule
                bleue pleine #2B579A / texte blanc, pour la mettre en
                avant comme un badge cliquable (parité visuelle avec les
                pills de statut). */}
            <div className="mt-0.5">
              {doc.reference ? (
                <span
                  className="inline-flex items-center h-6 px-2.5 rounded-full text-[12px] font-bold tabular-nums tracking-wide"
                  style={{ background: "#2B579A", color: "#FFFFFF", letterSpacing: "0.02em" }}
                  data-testid="doc-reference-pill"
                >
                  {doc.reference}
                </span>
              ) : (
                <span className="text-sm font-semibold text-muted-foreground">—</span>
              )}
            </div>
          </div>
          {_totalSteps > 0 && (
            <div className="px-3" data-testid="doc-reference-steps-cell">
              <div className="text-[9.5px] uppercase tracking-widest text-muted-foreground">Étapes</div>
              <div className="text-sm mt-0.5 tabular-nums">
                <span className="font-semibold text-white/90">{_closedSteps}</span>
                <span className="text-white/40">/{_totalSteps}</span>
                <span className="text-muted-foreground ml-1">validées</span>
              </div>
            </div>
          )}
          <div className="px-3">
            <div className="text-[9.5px] uppercase tracking-widest text-muted-foreground">Créé le</div>
            <div className="text-sm mt-0.5 text-white/85">
              {fmtDateTime(doc.created_at) || "—"}
            </div>
          </div>
          {_totalSteps > 0 && (
            <div className="px-3" data-testid="doc-reference-started-cell">
              <div className="text-[9.5px] uppercase tracking-widest text-muted-foreground">Débuté le</div>
              <div className="text-sm mt-0.5 text-white/85">
                {_debuteLe ? fmtDateTime(_debuteLe) : <span className="text-muted-foreground italic">—</span>}
              </div>
            </div>
          )}
          <div className="px-3" data-testid="doc-reference-lastact-cell">
            <div className="text-[9.5px] uppercase tracking-widest text-muted-foreground">Dernière activité</div>
            <div className="text-sm mt-0.5 text-white/85">
              {_derniereActivite ? fmtDateTime(_derniereActivite) : "—"}
            </div>
          </div>
          <div className="px-3">
            <div className="text-[9.5px] uppercase tracking-widest text-muted-foreground">Par</div>
            <div className="mt-1">
              {_creator ? (
                <UserAvatar
                  user={_creator}
                  size={26}
                  variant="initials"
                  subtitle={_creator.id === user?.id ? "Vous — Créateur" : "Créateur du document"}
                  testid="doc-reference-creator-avatar"
                />
              ) : (
                <span className="text-sm text-muted-foreground italic">—</span>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* --- Tabs (Détails / Historique) -------------------------------- */}
      <OutlookTabs module="documents" value={tab} onChange={setTab} testId="doc-detail-tabs" style={{ padding: 4, background: "#101013", border: "1px solid #26262b", borderRadius: 12, width: "fit-content" }}
        tabs={[{ key: "details", label: "Détails & workflow", icon: "doc-details", testId: "doc-tab-details" }, { key: "history", label: "Historique", icon: "history", testId: "doc-tab-history" }]} />

      {tab === "history" ? (
        <HistoryPanel docId={doc.id} doc={doc} usersById={usersById} isAdmin={isAdmin} />
      ) : (
        <>
      {/* --- Custom values card ----------------------------------------- */}
      {type && (
        <Card className="p-3 bg-card border-vertec-borderDark" data-testid="doc-values-card">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{isDossier ? "Champs du document" : "Détails"}</div>
            {isDossier && isOuvert && canEdit && (
              <div className="flex items-center" style={{ gap: 6 }}>
                {fieldsEditMode && <button type="button" onClick={() => setDossierDlg({ kind: "field" })} className="inline-flex items-center df-btn" style={{ gap: 5, background: "rgba(53,104,176,.15)", border: "0.5px solid rgba(53,104,176,.4)", color: "#7ab0e0", padding: "6px 12px", borderRadius: 7, fontSize: 11, fontWeight: 500, cursor: "pointer", fontFamily: "Tahoma, sans-serif" }} data-testid="dossier-add-field-btn"><DfPlus size={12} />Ajouter un champ</button>}
                {fieldsEditMode
                  ? <button type="button" onClick={() => setFieldsEditMode(false)} className="inline-flex items-center" style={{ gap: 5, background: "rgba(42,154,122,.15)", border: "0.5px solid rgba(42,154,122,.4)", color: "#37c99a", padding: "6px 12px", borderRadius: 7, fontSize: 11, fontWeight: 500, cursor: "pointer", fontFamily: "Tahoma, sans-serif" }} data-testid="dossier-fields-done-btn"><DfCheck size={12} />Terminer</button>
                  : <button type="button" onClick={() => setFieldsEditMode(true)} className="inline-flex items-center" style={{ gap: 5, background: "rgba(53,104,176,.15)", border: "0.5px solid rgba(53,104,176,.4)", color: "#7ab0e0", padding: "6px 12px", borderRadius: 7, fontSize: 11, fontWeight: 500, cursor: "pointer", fontFamily: "Tahoma, sans-serif" }} data-testid="dossier-fields-edit-btn"><DfPencil size={12} />Modifier</button>}
              </div>
            )}
          </div>
          <div className="grid grid-cols-4 gap-3">
            {/* Champ Siège / Projet — méta-donnée native du document, pas
                dans type.fields. Affichée en premier pour rester en tête
                (spec 2026-07-17 decies point 6). */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Siège / Projet</div>
              <div className="text-sm mt-0.5">
                {doc.project_id
                  ? (projects.find((p) => p.id === doc.project_id)?.name || "—")
                  : <span className="text-muted-foreground italic">—</span>}
              </div>
            </div>
            {(type.fields || []).map((f) => {
              // Spec rev159 (2026-02-07) — sur les documents SANS étape
              // (mode simple ou 0 étape), les champs `file` doivent
              // apparaître uniquement dans le bloc SUIVI (rendu par
              // `SimpleDocumentBlock`), pas ici dans « Détails ». Sur
              // les docs AVEC étapes, on garde la chip dans Détails
              // (elle représente une valeur de doc, pas d'étape).
              const isStepless = (doc.workflow_mode || "") === "simple" || (doc.steps || []).length === 0;
              if (isStepless && f.type === "file") return null;
              return (
              <div key={f.id || f.key}>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{f.label}</div>
                <div className="text-sm mt-0.5">
                  {(() => {
                    if (f.type !== "file") return _fmtValue(f, doc.values?.[f.key]);
                    // Spec rev158 (2026-02-07) — rendu robuste pour les
                    // champs "file" au niveau document (surtout docs sans
                    // étape) :
                    //   • Cherche une liste ou un dict dans values[key].
                    //   • Cherche aussi une entrée legacy dans
                    //     `filenames` / `field_filenames` (string ou
                    //     liste), pour docs anciens créés avant que
                    //     l'objet complet ne soit stocké dans values.
                    //   • Fabrique un nom de fichier lisible en cascade :
                    //     original_filename → storage_path (basename) →
                    //     valeur brute (si string) → « fichier » (dernier
                    //     recours).
                    //   • URL toujours dirigée sur l'endpoint qui existe
                    //     réellement côté backend : `/documents/{id}/
                    //     field-file/{key}?idx=N`.
                    const raw = doc.values?.[f.key];
                    const legacy = doc.filenames?.[f.key] || doc.field_filenames?.[f.key];
                    const basename = (s) => (typeof s === "string"
                      ? s.split(/[\\/]/).pop()
                      : "");
                    const pickName = (entry, legacyEntry) => {
                      if (typeof entry === "string" && entry) return entry;
                      if (entry && typeof entry === "object") {
                        return entry.original_filename
                          || basename(entry.storage_path)
                          || (typeof legacyEntry === "string" ? legacyEntry : "")
                          || basename(entry.url)
                          || "fichier";
                      }
                      if (typeof legacyEntry === "string" && legacyEntry) return legacyEntry;
                      return "fichier";
                    };
                    let entries = [];
                    if (Array.isArray(raw)) {
                      entries = raw
                        .map((e, i) => ({ e, i, legacy: Array.isArray(legacy) ? legacy[i] : legacy }))
                        .filter(({ e }) => !!e);
                    } else if (raw) {
                      entries = [{ e: raw, i: 0, legacy }];
                    } else if (legacy) {
                      // Uniquement le nom en legacy (pas d'objet dans values).
                      const list = Array.isArray(legacy) ? legacy : [legacy];
                      entries = list
                        .map((fn, i) => ({ e: fn, i, legacy: fn }))
                        .filter(({ e }) => !!e);
                    }
                    if (!entries.length) return _fmtValue(f, raw);
                    const files = entries.map(({ e, i, legacy: lg }) => ({
                      filename: pickName(e, lg),
                      url: `/documents/${doc.id}/field-file/${encodeURIComponent(f.key)}?idx=${i}`,
                      key: `${f.key}-${i}`,
                    }));
                    return (
                      <FileChipsGroup
                        files={files}
                        onView={(fx) => openAuthenticatedUrl(fx.url, { filename: fx.filename })}
                        onDownload={(fx) => {
                          const dl = buildDownloadHandler(fx.url, { filename: fx.filename });
                          return typeof dl === "function" ? dl() : undefined;
                        }}
                        testid={`file-chip-${f.key}`}
                      />
                    );
                  })()}
                </div>
              </div>
              );
            })}
            {isDossier && (doc.extra_fields || []).map((f) => (
              <div key={f.key} className="dossier-cf group" style={{ background: "linear-gradient(90deg, rgba(53,104,176,.06), transparent)", borderLeft: "2px solid #3568B0", paddingLeft: 10, borderRadius: 4 }} data-testid={`dossier-extra-field-${f.key}`}>
                <div className="text-[9px] uppercase tracking-widest text-muted-foreground truncate">{f.label}</div>
                <div style={{ fontSize: 12, color: "#ececf0", fontWeight: 500 }} className="mt-0.5" data-testid={`dossier-extra-field-value-${f.key}`}>{f.type === "checkbox" ? (doc.values?.[f.key] ? "Oui" : "Non") : f.type === "money" && doc.values?.[f.key] != null && doc.values?.[f.key] !== "" ? `${Number(doc.values[f.key]).toLocaleString("fr-FR")} DA` : f.type === "date" && doc.values?.[f.key] ? fmtDate(doc.values[f.key]) : (doc.values?.[f.key] ?? "") === "" ? <span className="text-muted-foreground italic">—</span> : String(doc.values[f.key])}</div>
                {fieldsEditMode && isOuvert && canEdit && (
                  <div className="flex items-center justify-end dossier-cf-actions" style={{ gap: 4, marginTop: 4, opacity: 0, transition: "opacity .12s" }}>
                    <button type="button" onClick={() => setDossierDlg({ kind: "field", field: f, value: doc.values?.[f.key] })} title="Modifier" className="inline-flex items-center justify-center df-edit" style={{ width: 20, height: 20, borderRadius: 4, background: "rgba(255,255,255,.05)", color: "#a4a4ae", border: "none", cursor: "pointer" }} data-testid={`dossier-extra-field-edit-${f.key}`}><DfPencil size={10} /></button>
                    <button type="button" onClick={() => setDossierDlg({ kind: "delField", field: f })} title="Supprimer" className="inline-flex items-center justify-center df-del" style={{ width: 20, height: 20, borderRadius: 4, background: "rgba(255,255,255,.05)", color: "#a4a4ae", border: "none", cursor: "pointer" }} data-testid={`dossier-extra-field-remove-${f.key}`}><DfTrash size={10} /></button>
                  </div>
                )}
              </div>
            ))}
            <style>{`.dossier-cf:hover .dossier-cf-actions{opacity:1!important}.df-edit:hover{background:rgba(53,104,176,.15)!important;color:#7ab0e0!important}.df-del:hover{background:rgba(220,38,38,.15)!important;color:#DC2626!important}.df-btn:hover{background:rgba(53,104,176,.22)!important}`}</style>
          </div>
        </Card>
      )}

      {/* --- Workflow (avec étapes) OU bloc "Document simple" (sans étape) --
          Spec rev128 (2026-02-05). Le rendu diverge selon :
            • workflow_mode === "simple"        → SimpleDocumentBlock
            • steps.length === 0 (legacy/edge)  → SimpleDocumentBlock
            • sinon                             → Workflow enrichi (donut,
              barre segmentée, prochaine action, cartes d'étapes stylées). */}
      {isDossier ? (
        <DossierPieces doc={doc} canEdit={canEdit && !isCancelled} onChanged={(d) => { if (d) setDoc(d); else load(); }} usersById={usersById} />
      ) : ((doc.workflow_mode || "") === "simple" || (doc.steps || []).length === 0) ? (
        <SimpleDocumentBlock doc={doc} usersById={usersById} />
      ) : (
        (() => {
          const steps = doc.steps || [];
          const isDocDone = ["terminee", "termine", "cloture", "annule"].includes(doc.status) || isCancelled;
          // Spec iter218 (2026-02-28) — le premier index ni clos ni supprimé.
          const currentIdx = isDocDone
            ? -1
            : (() => {
                const idx = steps.findIndex((s) => !s.closed_at && !s.deleted_at);
                if (idx >= 0) return idx;
                return typeof doc.current_step_index === "number" ? doc.current_step_index : -1;
              })();
          return (
            <Card
              className="p-5 bg-card border-vertec-borderDark relative"
              data-testid="doc-workflow-card"
              style={
                // Spec rev148 (2026-02-06) + iter243 (2026-08-31) — le
                // bloc Workflow entier est grisé si le doc est Suspendu,
                // Annulé ou Clôturé. Un tag coloré apparaît en haut à
                // droite pour préciser l'état (Gelé / Annulé / Clôturé).
                isSuspended ? { opacity: 0.5, pointerEvents: "none" } : (isCloture || isCancelled) ? { opacity: 0.92 } : undefined
              }
            >
              {isSuspended && (
                <span
                  className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded"
                  style={{
                    background: "#2A1F14",
                    color: "#F97316",
                    border: "1px solid rgba(249,115,22,0.45)",
                    pointerEvents: "auto",
                  }}
                  data-testid="doc-workflow-frozen-tag"
                >
                  <PauseCircle size={10} strokeWidth={2.6} /> Gelé
                </span>
              )}
              {isCancelled && (
                <span
                  className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded"
                  style={{
                    background: "#2A2A2E",
                    color: "#9CA3AF",
                    border: "1px solid rgba(156,163,175,0.45)",
                    pointerEvents: "auto",
                  }}
                  data-testid="doc-workflow-cancelled-tag"
                >
                  <XCircle size={10} strokeWidth={2.6} /> Annulé
                </span>
              )}
              {/* Spec iter95 (Feb 2026) — Suppression du gros donut
                  central + libellé « prochaine action » au-dessus de
                  la timeline : le donut d'entête (44 px, adaptatif)
                  porte déjà le % et l'étape en cours figure dans le
                  sous-titre du header + dans la timeline juste en
                  dessous. On saute directement au stepper vertical. */}
              {/* --- Vertical stepper --- */}
              <div className="space-y-1">
                {steps.map((st, idx) => {
                  const respIds = Array.isArray(st.snap_responsible_user_ids) && st.snap_responsible_user_ids.length
                    ? st.snap_responsible_user_ids
                    : (st.snap_responsible_user_id ? [st.snap_responsible_user_id] : []);
                  // iter170 — sans responsable explicite : ouverte (droit Valider) ; sinon réservée aux responsables + Premier Admin.
                  const canValidateStep = !respIds.length || !!user?.is_first_admin || respIds.includes(user?.id);
                  const responsibleNames = respIds.map((id) => {
                    const u = usersById[id];
                    return u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email : "—";
                  }).join(", ");
                  return (
                  <StepRow
                    key={st.id}
                    step={st}
                    index={idx}
                    isCurrent={idx === currentIdx && !st.closed_at && !st.deleted_at && !isDocDone}
                    isPast={!!st.closed_at && !st.deleted_at}
                    isDeleted={!!st.deleted_at}
                    isStruck={!!st.struck_at || (isCancelled && !st.closed_at)}
                    canEdit={canEdit && !isCancelled && !isCloture}
                    canValidateStep={canValidateStep}
                    responsibleNames={responsibleNames}
                    responsibleIds={respIds}
                    isAdmin={isAdmin && !isCloture}
                    docId={doc.id}
                    docReference={doc.reference}
                    typeId={doc.type_id}
                    usersById={usersById}
                    onChanged={setDoc}
                    isFirst={idx === 0}
                    isLast={idx === steps.length - 1}
                    currentIdx={currentIdx}
                    justCorrected={justCorrectedStep === idx}
                  />
                  );
                })}
              </div>
            </Card>
          );
        })()
      )}

      <MarkAsTerminatedCard doc={doc} user={user} onChanged={setDoc} />
      <SubmitForVerificationCard doc={doc} user={user} onChanged={setDoc} />
      <VerificationDecisionCard doc={doc} user={user} onChanged={setDoc} />
      {/* Spec rev148 (2026-02-06) — retrait complet du bloc « Action
          administrateur » (SuspendCard). L'action Suspendre passe
          désormais uniquement par le bouton d'en-tête + une modale de
          motif dédiée (rendue plus bas dans ce composant). */}

      {/* --- Spec rev149 (2026-02-06) — Bloc « Tâches liées » retiré.
          Les tâches sont désormais visibles uniquement via les badges
          cliquables dans l'en-tête (LinkedTaskBadge, Rev148). --- */}
        </>
      )}

      {/* --- Edit dialog (opened from the header "Modifier" button) ------- */}
      <DocumentEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        doc={doc}
        docType={isDossier && type ? { ...type, fields: [...(type.fields || []), ...(doc.extra_fields || [])] } : type}
        projects={(projects || []).filter((p) => p.in_choices !== false)}
        onSaved={() => { setEditOpen(false); load(); }}
      />
      {dossierDlg?.kind === "field" && <DossierFieldDialog docId={doc.id} field={dossierDlg.field} currentValue={dossierDlg.value} onClose={() => setDossierDlg(null)} onSaved={(d) => setDoc(d)} />}
      <DeskConfirm open={dossierDlg?.kind === "delField"} title="Supprimer ce champ ?" message={dossierDlg?.field ? `${dossierDlg.field.label} · valeur actuelle : ${(doc.values?.[dossierDlg.field.key] ?? "—") === "" ? "—" : String(doc.values?.[dossierDlg.field.key] ?? "—")}` : ""} confirmLabel="Supprimer" onClose={() => setDossierDlg(null)} onConfirm={() => dossierCall(() => api.delete(`/documents/${doc.id}/dossier/fields/${encodeURIComponent(dossierDlg.field.key)}`), "Champ supprimé")} testId="dossier-field-delete-confirm" />
      {dossierDlg === "close" && <CloseReopenDialog mode={dossierDlg} counts={dossierCounts} busy={dossierBusy} onClose={() => setDossierDlg(null)} onSubmit={(reason) => dossierCall(() => api.post(`/documents/${doc.id}/dossier/${dossierDlg}`, { reason: reason || null }), dossierDlg === "close" ? "Dossier clôturé" : "Dossier ré-ouvert")} />}

      {/* --- Task creation dialog with document link (spec rev16 · J) --- */}
      <TaskCreateDialog
        open={linkTaskOpen}
        onOpenChange={setLinkTaskOpen}
        projects={(projects || []).filter((p) => p.in_choices !== false)}
        users={users}
        linkedDocumentId={doc?.id}
        initialSubject={type && doc ? `${type.name} — ${doc.title}` : (doc?.title || "")}
        initialProjectId={doc?.project_id || ""}
        initialChecklist={(() => {
          // Suggestions par défaut : les étapes non-fermées et non-supprimées,
          // avec une priorité aux étapes en retard (is_late = True). Chaque
          // libellé est préfixé « Relancer » ou « Traiter » pour rendre
          // l'action explicite dans la checklist.
          // Spec iter218 (2026-02-28) — filtre les tombstones.
          const items = [];
          (doc?.steps || []).forEach((s) => {
            if (s.closed_at || s.deleted_at) return;
            const verb = s.is_late ? "Relancer" : "Traiter";
            items.push(`${verb} — ${s.label}`);
          });
          return items;
        })()}
        onCreated={() => { setLinkTaskOpen(false); load(); toast.success("Tâche liée créée"); }}
      />

      {/* Spec rev148 (2026-02-06) — Modale de suspension avec motif
          obligatoire (bordure orange #F97316). Réservée aux Admin.
          Spec iter208 (2026-02-28) — ajout d'une dropzone facultative
          pour attacher un ou plusieurs justificatifs au moment de la
          suspension (rapport, échange email, courrier, etc.). */}
      <Dialog open={suspendOpen} onOpenChange={(v) => {
        if (suspendSaving) return;
        setSuspendOpen(v);
        if (!v) { setSuspendReason(""); suspendFileQueue.reset(); }
      }}>
        <DialogContent
          className="max-w-[560px]"
          style={{
            border: "1.5px solid rgba(249,115,22,0.55)",
            boxShadow: "0 0 24px rgba(249,115,22,0.20)",
          }}
          data-testid="doc-suspend-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[15px]">
              <PauseCircle size={16} style={{ color: "#F97316" }} />
              Suspendre ce document
            </DialogTitle>
            <DialogDescription className="text-[12.5px] pt-1" style={{ color: "#8B8F94" }}>
              Toute action sera bloquée jusqu'à la reprise.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <label className="block text-[10px] uppercase tracking-widest mb-1.5" style={{ color: "#8B8F94" }}>
              Motif <span style={{ color: "#F97316" }}>*</span>
            </label>
            <Textarea
              autoFocus
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="Expliquez pourquoi ce document est suspendu…"
              rows={4}
              data-field="suspend-reason"
              className={requiredClass(suspendReq.has("suspend-reason"))}
              data-testid="doc-suspend-reason"
            />
            <FieldError show={suspendReq.has("suspend-reason")} testId="doc-suspend-reason-error" />
          </div>
          <div className="mt-4">
            <label className="block text-[10px] uppercase tracking-widest mb-1.5 flex items-center gap-1.5" style={{ color: "#8B8F94" }}>
              <Paperclip size={11} /> Justificatifs (optionnel)
            </label>
            <FileOrScanInput
              entries={suspendFileQueue.entries}
              onFiles={suspendFileQueue.enqueue}
              onCancel={suspendFileQueue.cancel}
              onRemove={suspendFileQueue.remove}
              multiple
              testIdPrefix="doc-suspend-attach"
            />
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setSuspendOpen(false)} disabled={suspendSaving} data-testid="doc-suspend-cancel">
              Annuler
            </Button>
            <Button
              onClick={submitSuspend}
              disabled={suspendSaving}
              data-incomplete={!suspendReason.trim() || undefined}
              className="text-white border-0 disabled:opacity-50"
              style={{ background: "#F97316" }}
              data-testid="doc-suspend-confirm"
            >
              <PauseCircle size={13} className="mr-1" />
              {suspendSaving ? "Suspension…" : "Suspendre"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Spec iter206 pt.2 (2026-02-28) — Modale d'annulation définitive.
          Bandeau d'avertissement rouge, motif obligatoire, action
          irréversible (aucun retour arrière possible côté backend).
          Spec iter208 (2026-02-28) — dropzone justificatifs. */}
      <Dialog open={cancelOpen} onOpenChange={(v) => {
        if (cancelSaving) return;
        setCancelOpen(v);
        if (!v) { setCancelReason(""); cancelFileQueue.reset(); }
      }}>
        <DialogContent
          className="max-w-[560px]"
          style={{
            border: "1.5px solid rgba(239,68,68,0.55)",
            boxShadow: "0 0 24px rgba(239,68,68,0.20)",
          }}
          data-testid="doc-cancel-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[15px]">
              <XCircle size={16} style={{ color: "#EF4444" }} />
              Annuler « {doc.reference} — {doc.title} » ?
            </DialogTitle>
          </DialogHeader>
          <div
            className="mt-2 rounded-md p-3 flex items-start gap-2"
            style={{
              background: "rgba(239,68,68,0.10)",
              border: "1px solid rgba(239,68,68,0.35)",
            }}
          >
            <AlertTriangle size={16} style={{ color: "#EF4444", flexShrink: 0, marginTop: 2 }} />
            <div className="text-[12.5px] leading-relaxed" style={{ color: "#F5D2D2" }}>
              <b>Cette action verrouille immédiatement le document.</b>{" "}
              Toutes les modifications seront bloquées (étapes, upload,
              édition). Seul un administrateur pourra ensuite le{" "}
              <b>réactiver</b> depuis la fiche.
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-[11px] uppercase tracking-widest text-muted-foreground mb-1">
              Motif d'annulation <span style={{ color: "#EF4444" }}>*</span>
            </label>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              placeholder="Préciser la raison de l'annulation (obligatoire)"
              data-field="cancel-reason"
              className={requiredClass(cancelReq.has("cancel-reason"))}
              data-testid="doc-cancel-reason-input"
              autoFocus
            />
            <FieldError show={cancelReq.has("cancel-reason")} testId="doc-cancel-reason-error" />
          </div>
          <div className="mt-4">
            <label className="block text-[10px] uppercase tracking-widest mb-1.5 flex items-center gap-1.5" style={{ color: "#8B8F94" }}>
              <Paperclip size={11} /> Justificatifs (optionnel)
            </label>
            <FileOrScanInput
              entries={cancelFileQueue.entries}
              onFiles={cancelFileQueue.enqueue}
              onCancel={cancelFileQueue.cancel}
              onRemove={cancelFileQueue.remove}
              multiple
              testIdPrefix="doc-cancel-attach"
            />
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setCancelOpen(false)} disabled={cancelSaving} data-testid="doc-cancel-close">
              Fermer
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                if (!cancelReq.validate()) return;
                setCancelSaving(true);
                try {
                  await api.post(`/documents/${doc.id}/status`, {
                    status: "annule", reason: cancelReason.trim(),
                  });
                  let attachErrors = 0;
                  await cancelFileQueue.uploadAll(async (file, { onProgress, signal }) => {
                    const form = new FormData();
                    form.append("file", file);
                    try {
                      await api.post(
                        `/documents/${doc.id}/action-attach?action=cancel`,
                        form,
                        {
                          headers: { "Content-Type": "multipart/form-data" },
                          signal,
                          onUploadProgress: (evt) => {
                            if (!evt.total) return;
                            onProgress(Math.round((evt.loaded / evt.total) * 100), evt.loaded);
                          },
                        },
                      );
                    } catch (e) { attachErrors += 1; throw e; }
                  });
                  toast.success("Document annulé", {
                    description: attachErrors > 0
                      ? `${attachErrors} justificatif(s) n'ont pas pu être envoyés.`
                      : undefined,
                  });
                  setCancelOpen(false);
                  setCancelReason("");
                  cancelFileQueue.reset();
                  await load();
                } catch (e) {
                  toast.error("Impossible d'annuler", { description: e.response?.data?.detail });
                } finally { setCancelSaving(false); }
              }}
              disabled={cancelSaving}
              data-incomplete={!cancelReason.trim() || undefined}
              data-testid="doc-cancel-confirm"
              style={{ background: "#EF4444", color: "#FFF" }}
            >
              {cancelSaving ? "Annulation…" : "Annuler définitivement"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Spec iter243 (2026-08-31) — Dialog RÉACTIVATION du document
          annulé. Visible uniquement pour Admin / First Admin (le
          bouton d'appel est déjà gated). Motif optionnel — le backend
          l'accepte mais ne l'exige pas. */}
      <Dialog open={reactivateOpen} onOpenChange={(v) => {
        if (reactivateSaving) return;
        setReactivateOpen(v);
        if (!v) setReactivateReason("");
      }}>
        <DialogContent
          className="max-w-[520px]"
          style={{
            border: "1.5px solid rgba(74,222,128,0.55)",
            boxShadow: "0 0 24px rgba(74,222,128,0.20)",
          }}
          data-testid="doc-reactivate-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[15px]">
              <PlayCircle size={16} style={{ color: "#4ADE80" }} />
              Réactiver « {doc.reference} — {doc.title} » ?
            </DialogTitle>
          </DialogHeader>
          <div
            className="mt-2 rounded-md p-3 flex items-start gap-2"
            style={{
              background: "rgba(74,222,128,0.10)",
              border: "1px solid rgba(74,222,128,0.35)",
            }}
          >
            <AlertTriangle size={16} style={{ color: "#4ADE80", flexShrink: 0, marginTop: 2 }} />
            <div className="text-[12.5px] leading-relaxed" style={{ color: "#D6F5DE" }}>
              La mention « Annulé » est retirée, le document reprend son
              cycle normal et les étapes barrées redeviennent modifiables.
              L'annulation initiale (motif, date, auteur) reste tracée
              dans l'historique ; la réactivation s'y ajoute.
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-[11px] uppercase tracking-widest text-muted-foreground mb-1">
              Motif de réactivation <span className="text-red-500">*</span>
            </label>
            <Textarea
              value={reactivateReason}
              onChange={(e) => setReactivateReason(e.target.value)}
              rows={3}
              placeholder="Ex : annulation effectuée par erreur — dossier à reprendre"
              data-field="reactivate-reason"
              className={requiredClass(reactivateReq.has("reactivate-reason"))}
              data-testid="doc-reactivate-reason-input"
              autoFocus
            />
            <FieldError show={reactivateReq.has("reactivate-reason")} testId="doc-reactivate-reason-error" />
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setReactivateOpen(false)} disabled={reactivateSaving} data-testid="doc-reactivate-close">
              Fermer
            </Button>
            <Button
              onClick={submitReactivate}
              disabled={reactivateSaving}
              data-testid="doc-reactivate-confirm"
              style={{ background: "#4ADE80", color: "#0A1A0E", fontWeight: 700 }}
            >
              {reactivateSaving ? "Réactivation…" : "Réactiver le document"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter415 pt 2 — suppression depuis la fiche : DeskConfirm R2, même contenu que depuis la liste (DeleteDocumentDialog). */}
      <DeskConfirm open={deleteOpen} title={<>Supprimer <span className="font-mono" style={{ color: "#5BA3F5" }}>{doc.reference}</span> ?</>}
        message="Le document ne sera pas perdu : il part dans Archives → Supprimés avec votre motif, et un administrateur pourra le restaurer."
        withReason reasonPlaceholder="Motif de la suppression (obligatoire)" busy={deleteSaving} confirmLabel={deleteSaving ? "Archivage…" : "Archiver"}
        onClose={() => { if (!deleteSaving) setDeleteOpen(false); }} onConfirm={submitDelete} testId="doc-delete-dialog" />

      {/* Spec rev149 (2026-02-06) — Modale « Corriger » (globale ou par
          étape). Bordure orange, textarea explication obligatoire,
          bouton Continuer orange désactivé tant que vide. */}
      <Dialog open={corrigerOpen} onOpenChange={(v) => !corrigerSaving && setCorrigerOpen(v)}>
        <DialogContent
          className="max-w-[520px]"
          style={{
            border: "1.5px solid rgba(249,115,22,0.55)",
            boxShadow: "0 0 24px rgba(249,115,22,0.20)",
          }}
          data-testid="doc-corriger-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[15px]">
              <AlertTriangle size={16} style={{ color: "#F97316" }} />
              {corrigerScope?.scope === "step"
                ? `Corriger l'étape « ${corrigerScope?.stepLabel || `#${corrigerScope?.stepIndex + 1}`} »`
                : corrigerScope?.scope === "piece"
                ? `Corriger la pièce « ${corrigerScope?.stepLabel || "Pièce"} »`
                : "Corriger ce document"}
            </DialogTitle>
            <DialogDescription className="text-[12.5px] pt-1" style={{ color: "#8B8F94" }}>
              {corrigerScope?.scope === "step"
                ? "Cette étape a déjà été validée. Une explication est requise avant d'apporter une correction — elle sera tracée dans l'historique du document."
                : corrigerScope?.scope === "piece"
                ? "Une explication est requise pour demander la correction de cette pièce — elle sera tracée dans l'historique et notifiée à la personne qui l'a ajoutée ainsi qu'aux responsables du type."
                : isDossier
                ? "Ce dossier est clôturé. Une explication est requise pour le repasser en « À corriger » (champs et pièces redeviennent modifiables) — elle sera tracée dans l'historique et notifiée aux personnes concernées."
                : "Ce document est en attente de vérification. Une explication est requise pour repartir en correction — elle sera tracée dans l'historique."}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <label className="block text-[10px] uppercase tracking-widest mb-1.5" style={{ color: "#8B8F94" }}>
              Explication <span style={{ color: "#F97316" }}>*</span>
            </label>
            <Textarea
              autoFocus
              value={corrigerReason}
              onChange={(e) => setCorrigerReason(e.target.value)}
              placeholder="Décrivez la raison de la correction (obligatoire, tracé dans l'historique)…"
              rows={4}
              data-testid="doc-corriger-reason"
            />
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setCorrigerOpen(false)} disabled={corrigerSaving} data-testid="doc-corriger-cancel">
              Annuler
            </Button>
            <Button
              onClick={submitCorriger}
              disabled={!corrigerReason.trim() || corrigerSaving}
              className="text-white border-0 disabled:opacity-50"
              style={{ background: "#F97316" }}
              data-testid="doc-corriger-continue"
            >
              <AlertTriangle size={13} className="mr-1" />
              {corrigerSaving ? "Enregistrement…" : "Continuer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}



/**
 * LinkedTasksCard — spec 2026-02-XX rev16 (point J.5).
 * Résumé compact des tâches créées depuis ce document. Reprend le style
 * des KPI Tâches existantes : mini-cartes avec liseré coloré par statut.
 * Cliquable pour naviguer vers Tâches?scope=linked_to_document.
 */
function LinkedTasksCard({ summary, onOpenCreate }) {
  const navigate = useNavigate();
  const counts = summary?.counts || {};
  const total = summary?.total || 0;
  const STATUS_LIST = [
    { key: "waiting",     label: "En attente",     color: "#F59E0B" },
    { key: "in_progress", label: "En cours",       color: "#3B82F6" },
    { key: "to_verify",   label: "À vérifier",     color: "#8B5CF6" },
    { key: "closed",      label: "Clôturées",      color: "var(--status-cloture-docs)" },
  ];
  const nonZero = STATUS_LIST.filter((s) => (counts[s.key] || 0) > 0);
  return (
    <Card className="p-4 border-vertec-borderDark" data-testid="doc-linked-tasks">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileText size={14} className="text-vertec-gold" />
          <h3 className="text-sm font-semibold">Tâches liées</h3>
          {total > 0 && (
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
              · {total} au total
            </span>
          )}
        </div>
        {/* Spec rev148 (2026-02-06) — Bouton « + Créer » retiré.
            Indication discrète pour rediriger vers la barre du haut. */}
        <span className="text-[10.5px] italic flex items-center gap-1" style={{ color: "#6B7280" }}>
          ↑ Créer via le bouton en haut
        </span>
      </div>
      {total === 0 ? (
        <div className="text-xs text-muted-foreground italic py-4 text-center">
          Aucune tâche n'a encore été créée depuis ce document.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {nonZero.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => navigate(`/taches?scope=linked_to_document&status=${s.key}`)}
              className="text-left px-3 py-2 rounded-md hover:bg-white/[0.03] transition-colors"
              style={{
                background: `${s.color}0F`,
                borderLeft: `3px solid ${s.color}`,
                border: `1px solid ${s.color}22`,
              }}
              data-testid={`linked-tasks-kpi-${s.key}`}
              title={`Voir les tâches ${s.label.toLowerCase()}`}
            >
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                {s.label}
              </div>
              <div className="text-lg font-bold tabular-nums" style={{ color: s.color }}>
                {counts[s.key] || 0}
              </div>
            </button>
          ))}
        </div>
      )}
    </Card>
  );
}


// ---------------------------------------------------------------------------
// MarkAsTerminatedCard — visible when the doc is still `en_cours` AND all
// its workflow steps are closed. Presents an explicit "✓ Terminé" button
// so the creator (or an admin) can consciously flip the doc into the
// `terminee` state. Replaces the previous silent auto-transition
// (spec 2026-02-13).
// ---------------------------------------------------------------------------
/** Étapes validées avec champ obligatoire vide (calcul backend, ``blocking_required``). */
const _blockingTitle = (doc) => {
  const b = doc?.blocking_required || [];
  if (!b.length) return "";
  return "Impossible : " + b.map((x) => `l'étape « ${x.step_label} » a un champ obligatoire vide (${(x.missing || []).join(", ")})`).join(" ; ") + ".";
};

function MarkAsTerminatedCard({ doc, user, onChanged }) {
  const [submitting, setSubmitting] = useState(false);
  if (!doc || doc.status !== "en_cours") return null;
  const steps = doc.steps || [];
  // Spec iter218 (2026-02-28) — un doc est considéré "tout fermé"
  // lorsque toutes les étapes vivantes sont closes. Les tombstones
  // (deleted_at) ne bloquent pas la transition vers "Terminé".
  const allClosed = steps.length > 0 && steps.every((s) => s.closed_at || s.deleted_at);
  if (!allClosed) return null;
  const deletedCount = steps.filter((s) => s.deleted_at).length;
  const isCreator = user?.id === doc.created_by;
  const isAdmin = user?.role === "admin";
  if (!isCreator && !isAdmin) return null;

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/documents/${doc.id}/status`, { status: "terminee" });
      onChanged(data);
      toast.success("Document marqué comme Terminé", {
        description: "Vous pouvez maintenant le soumettre pour vérification",
      });
    } catch (err) {
      toast.error("Impossible", { description: err.response?.data?.detail || "" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card
      className="p-5 border"
      style={{
        background: "linear-gradient(180deg, rgba(234,179,8,0.06), rgba(234,179,8,0.015))",
        borderColor: "rgba(234,179,8,0.35)",
      }}
      data-testid="doc-mark-terminated-card"
    >
      <div className="flex items-start gap-4">
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: "rgba(234,179,8,0.15)", color: "#EAB308" }}
        >
          <CircleCheck size={22} strokeWidth={2.2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold" style={{ color: "#EAB308" }}>
            {deletedCount > 0
              ? `Toutes les étapes restantes sont validées (${deletedCount} supprimée${deletedCount > 1 ? "s" : ""})`
              : "Toutes les étapes sont validées"}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Cliquez sur « Terminé » pour figer le workflow et faire passer le document au statut Terminée.
          </div>
        </div>
        <button
          type="button"
          onClick={submit}
          disabled={submitting || !!(doc.blocking_required || []).length}
          title={_blockingTitle(doc)}
          data-blocked={(doc.blocking_required || []).length ? "true" : undefined}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-sm font-semibold text-white border-0 transition-all whitespace-nowrap"
          style={{
            background: submitting ? "#2A2A2A" : "#EAB308",
            color: submitting ? "#6B7280" : "#1B1B1B",
            boxShadow: submitting ? "none" : "0 2px 10px rgba(234,179,8,0.35)",
            cursor: submitting ? "not-allowed" : "pointer",
          }}
          data-testid="doc-mark-terminated-btn"
        >
          {submitting ? "Envoi…" : "✓ Terminé"}
        </button>
      </div>
    </Card>
  );
}


// ---------------------------------------------------------------------------
// VerificationDecisionCard — spec 2026-07-19 (bug 5 correctifs #2).
// Visible quand le doc est en statut `termine` (À vérifier). Propose 2
// actions au manager du créateur / admin : "Clôturer" (final) ou
// "À refaire" (renvoie en `en_cours` avec motif obligatoire).
// ---------------------------------------------------------------------------
function VerificationDecisionCard({ doc, user, onChanged }) {
  const { can: permsCan } = usePermissions();
  const canCloseRight = permsCan("documents", "close");
  const [busy, setBusy] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reason, setReason] = useState("");
  const navigate = useNavigate();
  if (!doc || doc.status !== "termine") return null;
  const isAdmin = user?.role === "admin" || user?.is_first_admin;
  const isManagerOfCreator = user?.id && doc?.creator_manager_id === user.id;
  if (!isAdmin && !isManagerOfCreator) return null;

  const doClose = async () => {
    setBusy(true);
    try {
      await api.post(`/documents/${doc.id}/status`, { status: "cloture" });
      toast.success("Document clôturé", { description: "Le workflow est terminé" });
      setTimeout(() => navigate("/documents"), 400);
    } catch (err) {
      toast.error("Clôture impossible", { description: err.response?.data?.detail || "" });
      setBusy(false);
    }
  };

  const doReopen = async () => {
    const r = reason.trim();
    if (r.length < 3) { toast.error("Motif requis (≥ 3 caractères)"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/documents/${doc.id}/reopen`, { reason: r });
      onChanged(data);
      toast.success("Renvoyé à refaire", { description: "Le créateur en est notifié" });
      setReopenOpen(false); setReason("");
    } catch (err) {
      toast.error("Renvoi impossible", { description: err.response?.data?.detail || "" });
    } finally { setBusy(false); }
  };

  return (
    <>
      <Card
        className="p-5 border"
        style={{
          // Spec rev157 (2026-02-07) — restylage :
          //   • Retrait de l'icône ronde à gauche (bloc plus épuré).
          //   • Bord GAUCHE mauve `#B983E8` de 3 px (rappel visuel du
          //     statut « à vérifier », cohérent avec le bouton mauve).
          //   • Fond neutre `#161616` conservé (aligné sur les autres
          //     blocs de la fiche).
          background: "#161616",
          borderColor: "#2A2A2A",
          borderLeft: "3px solid #B983E8",
        }}
        data-testid="doc-verification-decision-card"
      >
        <div className="flex items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold" style={{ color: "#B983E8" }}>
              Document à vérifier
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Validez la clôture définitive ou signalez une correction à apporter avec une explication.
            </div>
          </div>
          <div className="flex gap-2">
            {/* Bouton « Corriger » — inchangé (contour orange). */}
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent("doc-corriger-open", { detail: { scope: "document" } }))}
              disabled={busy}
              className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md text-sm font-semibold border transition-all"
              style={{ background: "#2A1F14", color: "#FB923C", borderColor: "#F97316" }}
              data-testid="doc-corriger-btn"
            >
              <AlertTriangle size={14} strokeWidth={2.4} /> Corriger
            </button>
            {/* Spec iter115 (Feb 2026) — bouton « Clôturer » aligné sur le
                style outline des boutons voisins (« Corriger » orange,
                « Supprimer » rouge) : fond sombre teinté vert + texte vert
                + bordure verte. Fini l'effet « déjà pressé » du fill plein.
                Couleurs cohérentes avec le badge Clôturé var(--status-cloture-docs). */}
            <button
              type="button"
              onClick={canCloseRight ? doClose : undefined}
              disabled={busy || !canCloseRight || !!(doc.blocking_required || []).length}
              title={!canCloseRight ? "Droit de clôture requis" : _blockingTitle(doc)}
              data-no-right={!canCloseRight || undefined}
              data-blocked={(doc.blocking_required || []).length ? "true" : undefined}
              className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md text-sm font-semibold border transition-all hover:brightness-125"
              style={{
                background: busy || !canCloseRight ? "#2A2A2A" : "#2D1F3D",
                color: busy || !canCloseRight ? "#6B7280" : "#C084FC",
                borderColor: busy || !canCloseRight ? "#2A2A2A" : "var(--status-cloture-docs)",
                cursor: busy || !canCloseRight ? "not-allowed" : "pointer",
              }}
              data-testid="doc-close-btn"
            >
              <LockIcon size={14} strokeWidth={2.4} /> {busy ? "…" : "Clôturer"}
            </button>
          </div>
        </div>
      </Card>

      {reopenOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60" onClick={() => setReopenOpen(false)}>
          <div className="bg-card border border-vertec-borderDark rounded-lg p-5 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="text-base font-semibold">Renvoyer à refaire</div>
            <div className="text-xs text-muted-foreground">Le créateur sera notifié avec ce motif.</div>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)}
              rows={4} placeholder="Motif (min. 3 caractères)…"
              className="w-full text-sm p-2 rounded bg-background border border-vertec-borderDark"
              data-testid="doc-reopen-reason" />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setReopenOpen(false)}
                className="h-9 px-3 rounded border border-vertec-borderDark text-sm">Annuler</button>
              <button type="button" onClick={doReopen} disabled={busy}
                className="h-9 px-3 rounded bg-status-danger text-white text-sm font-semibold"
                data-testid="doc-reopen-confirm">Renvoyer</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


// ---------------------------------------------------------------------------
// SubmitForVerificationCard — visible only when the doc is in `terminee`
// status AND the user is either its creator or an admin. Presents a single
// call to action to move the doc to `termine` (À vérifier) which notifies
// the manager. Spec 2026-02-12 · project_rules_spec.md §6.
// ---------------------------------------------------------------------------
function SubmitForVerificationCard({ doc, user, onChanged }) {
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  if (!doc || doc.status !== "terminee") return null;
  const isCreator = user?.id === doc.created_by;
  const isAdmin = user?.role === "admin";
  if (!isCreator && !isAdmin) return null;
  // Spec iter218 (2026-02-28) — reformulation quand certaines étapes sont
  // tombstonées : le libellé reflète la réalité (« étapes restantes »).
  const deletedCount = (doc.steps || []).filter((s) => s.deleted_at).length;

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/documents/${doc.id}/status`, { status: "termine" });
      // Spec 2026-07-19 (bug 5 correctifs #3) — message clair + redirection
      // automatique vers la liste globale des documents.
      toast.success("Ce document est passé en vérification", {
        description: "Votre manager en sera notifié",
      });
      setTimeout(() => navigate("/documents"), 500);
    } catch (err) {
      toast.error("Impossible de soumettre", { description: err.response?.data?.detail || "" });
      setSubmitting(false);
    }
  };

  return (
    <Card
      className="p-5 border"
      style={{
        background: "linear-gradient(180deg, rgba(234,179,8,0.08), rgba(234,179,8,0.02))",
        borderColor: "rgba(234,179,8,0.4)",
      }}
      data-testid="doc-submit-for-verification-card"
    >
      <div className="flex items-start gap-4">
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: "rgba(234,179,8,0.15)", color: "#EAB308" }}
        >
          <CheckCircle2 size={22} strokeWidth={2.2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold" style={{ color: "#EAB308" }}>
            {deletedCount > 0
              ? `Toutes les étapes restantes sont validées (${deletedCount} supprimée${deletedCount > 1 ? "s" : ""})`
              : "Toutes les étapes sont validées"}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Le document est passé automatiquement en « Terminée ». Revoyez votre travail puis soumettez-le pour vérification par votre manager.
          </div>
        </div>
        <button
          type="button"
          onClick={submit}
          disabled={submitting || !!(doc.blocking_required || []).length}
          title={_blockingTitle(doc)}
          data-blocked={(doc.blocking_required || []).length ? "true" : undefined}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-sm font-semibold text-white border-0 transition-all whitespace-nowrap"
          style={{
            background: submitting ? "#2A2A2A" : "#A855F7",
            color: submitting ? "#6B7280" : "#fff",
            boxShadow: submitting ? "none" : "0 2px 10px rgba(168,85,247,0.35)",
            cursor: submitting ? "not-allowed" : "pointer",
          }}
          data-testid="doc-submit-for-verification-btn"
        >
          {submitting ? "Envoi…" : "Soumettre pour vérification →"}
        </button>
      </div>
    </Card>
  );
}


// ---------------------------------------------------------------------------
// SuspendCard — spec 2026-07-17 octies (Hamza)
//
// Rend une carte compacte visible UNIQUEMENT pour les admins et le Premier
// Admin. Deux modes :
//   • Doc non suspendu  → bouton "Suspendre le document" (ouvre un dialog
//     avec motif obligatoire + rappel à date optionnel).
//   • Doc suspendu      → panneau récapitulatif (motif + date, + rappel s'il
//     y en a un) + bouton "Lever la suspension" (restauration statut).
// La carte n'apparaît PAS si le doc est déjà `cloture` (archivé, pas de
// suspension utile) — dès que la clôture finale a été validée, le doc
// est immuable.
// ---------------------------------------------------------------------------
function SuspendCard({ doc, user, onChanged }) {
  const [dlg, setDlg] = useState(false);
  const [reason, setReason] = useState("");
  const [remindAt, setRemindAt] = useState("");
  const [submitting, setSubmitting] = useState(false);
  if (!doc) return null;
  const isAdmin = hasRight(user, "documents.doc_status");
  if (!isAdmin) return null;
  if (doc.status === "cloture") return null;   // rien à faire sur un archive
  const isSuspended = doc.status === "suspendu";

  const openDialog = () => {
    setReason("");
    setRemindAt("");
    setDlg(true);
  };

  const submitSuspend = async () => {
    if (!reason.trim()) return;
    setSubmitting(true);
    try {
      const body = { status: "suspendu", reason: reason.trim() };
      if (remindAt) body.remind_at = remindAt;
      const { data } = await api.post(`/documents/${doc.id}/status`, body);
      onChanged(data);
      setDlg(false);
      toast.success("Document suspendu", {
        description: "Le workflow est gelé. Le créateur a été prévenu",
      });
    } catch (err) {
      toast.error("Impossible de suspendre", { description: err.response?.data?.detail || "" });
    } finally {
      setSubmitting(false);
    }
  };

  const submitResume = async () => {
    setSubmitting(true);
    try {
      // Restaure le statut d'avant suspension (fallback en_cours si vide).
      const target = doc.pre_suspend_status || "en_cours";
      const { data } = await api.post(`/documents/${doc.id}/status`, { status: target });
      onChanged(data);
      toast.success("Suspension levée", {
        description: `Le document revient au statut « ${target} »`,
      });
    } catch (err) {
      toast.error("Impossible de lever la suspension", { description: err.response?.data?.detail || "" });
    } finally {
      setSubmitting(false);
    }
  };

  if (isSuspended) {
    return (
      <Card
        className="p-5 border"
        style={{
          background: "linear-gradient(180deg, rgba(0,120,212,0.06), rgba(0,120,212,0.015))",
          borderColor: "rgba(0,120,212,0.35)",
        }}
        data-testid="doc-suspend-card-active"
      >
        <div className="flex items-start gap-4">
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: "rgba(0,120,212,0.18)", color: "#0078D4" }}
          >
            <PauseCircle size={22} strokeWidth={2.2} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold" style={{ color: "#5BA3E3" }}>
              Document suspendu · workflow gelé
            </div>
            <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
              {doc.suspend_reason && (
                <div><span className="uppercase tracking-widest text-[9px] mr-1.5">Motif</span>{doc.suspend_reason}</div>
              )}
              {doc.suspended_at && (
                <div>
                  <span className="uppercase tracking-widest text-[9px] mr-1.5">Depuis</span>
                  {fmtDate(doc.suspended_at)}
                </div>
              )}
              {doc.suspend_remind_at && (
                <div>
                  <span className="uppercase tracking-widest text-[9px] mr-1.5">Rappel</span>
                  {fmtDate(doc.suspend_remind_at)}
                </div>
              )}
            </div>
            <ActionAttachmentsBlock
              docId={doc.id}
              action="suspend"
              files={doc.suspend_attachments || []}
              accent="#5BA3E3"
            />
          </div>
          <button
            type="button"
            onClick={submitResume}
            disabled={submitting}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-sm font-semibold text-white border-0 transition-all whitespace-nowrap"
            style={{
              background: submitting ? "#2A2A2A" : "#22C55E",
              color: submitting ? "#6B7280" : "#fff",
              boxShadow: submitting ? "none" : "0 2px 10px rgba(34,197,94,0.35)",
              cursor: submitting ? "not-allowed" : "pointer",
            }}
            data-testid="doc-resume-btn"
            title="Rétablir le workflow"
          >
            <PlayCircle size={16} strokeWidth={2.4} />
            {submitting ? "…" : "Lever la suspension"}
          </button>
        </div>
      </Card>
    );
  }

  // Non-suspendu → bouton d'ouverture du dialog
  return (
    <>
      <Card
        className="p-4 border"
        style={{
          background: "linear-gradient(180deg, rgba(184,98,63,0.06), rgba(184,98,63,0.015))",
          borderColor: "rgba(184,98,63,0.30)",
        }}
        data-testid="doc-suspend-card-idle"
      >
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: "rgba(184,98,63,0.18)", color: "#F0906F" }}
          >
            <PauseCircle size={18} strokeWidth={2.2} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs" style={{ color: "#F0906F" }}>
              Action administrateur
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              Bloquer temporairement toute action sur ce document (motif obligatoire).
            </div>
          </div>
          <button
            type="button"
            onClick={openDialog}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold border transition-colors whitespace-nowrap"
            style={{
              background: "transparent",
              borderColor: "#B8623F80",
              color: "#F0906F",
            }}
            data-testid="doc-suspend-btn"
            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(184,98,63,0.10)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          >
            <PauseCircle size={13} strokeWidth={2.4} />
            Suspendre le document
          </button>
        </div>
      </Card>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent data-testid="doc-suspend-dialog" style={{ maxWidth: 520 }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PauseCircle size={18} style={{ color: "#F0906F" }} />
              Suspendre le document
            </DialogTitle>
            <DialogDescription>
              Le workflow sera gelé. Le créateur sera prévenu immédiatement.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                Motif *
              </label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Expliquez pourquoi ce document est suspendu…"
                rows={4}
                className="mt-1 resize-none"
                data-testid="doc-suspend-reason"
                autoFocus
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                Rappel à date (facultatif)
              </label>
              <Input
                type="date"
                value={remindAt}
                onChange={(e) => setRemindAt(e.target.value)}
                className="mt-1"
                data-testid="doc-suspend-remind"
              />
              <div className="text-[10px] text-muted-foreground mt-1">
                Une notification et un e-mail seront envoyés à cette date pour revoir la suspension.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)} disabled={submitting}
              data-testid="doc-suspend-cancel">
              Annuler
            </Button>
            <Button
              onClick={submitSuspend}
              disabled={!reason.trim() || submitting}
              className="text-white border-0"
              style={{
                background: (!reason.trim() || submitting) ? "#2A2A2A" : "#B8623F",
                color: (!reason.trim() || submitting) ? "#6B7280" : "#fff",
                boxShadow: (!reason.trim() || submitting) ? "none" : "0 2px 10px rgba(184,98,63,0.35)",
              }}
              data-testid="doc-suspend-confirm"
            >
              {submitting ? "Envoi…" : "Confirmer la suspension"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


// ---------------------------------------------------------------------------
// StepRow — one entry in the vertical stepper.
//
// Unified interaction model (spec 2026-02-10, project_rules_spec.md §7) :
//   • ALL required field types use an explicit "✓ Valider l'étape" button —
//     no more blur-based auto-close. The button is disabled while the
//     required field is empty; it becomes active as soon as the field is
//     filled.
//   • Once a step is closed, its value stays editable: a "Modifier" button
//     switches the row back to edit-mode; clicking "Valider" again persists
//     the new value (backend records a `step_value` / `file_replace` audit
//     event).
// ---------------------------------------------------------------------------
function StepRow({ adhoc = false, onRemoveAdhoc = null, step, index, isCurrent, isPast, isDeleted = false, isStruck = false, canEdit: canEditProp, canValidateStep = true, responsibleNames = "", responsibleIds = [], isAdmin = false, docId, docReference, typeId, onChanged, usersById, isFirst = false, isLast = false, currentIdx = -1, justCorrected = false }) {
  const { user: _authUser } = useAuth();
  const canCorriger = hasRight(_authUser, "documents.corriger");
  const canEdit = canEditProp && !isStruck;
  const stepLocked = !canValidateStep;
  const restrictedTitle = `Réservé à : ${responsibleNames || "—"}`;
  const [expanded, setExpanded] = useState(isCurrent || isPast);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [editing, setEditing] = useState(false);
  // Spec iter110 Partie 2 — navigation vers Paramètres > Utilisateurs
  // depuis le badge Responsable de l'étape courante.
  const navigate = useNavigate();
  // Spec rev132 (2026-02-05) — accès aux helpers d'ouverture/téléchargement
  // des fichiers pour rendre les chips fichiers SOUS la carte d'étape.
  const { openAuthenticatedUrl, buildDownloadHandler } = useFilePreview();

  // Multi-field state — spec 2026-02-12 §7.
  // `fieldValues[field.id]` holds the LOCAL draft for value-type fields
  // (text/number/date/money/dropdown/checkbox).
  // `stagedFiles[field.id]` holds a File object staged for upload on Valider
  // (used only in edit-mode of a CLOSED step; open-step file picks upload
  // immediately).
  const snapFields = useMemo(() => {
    return Array.isArray(step.snap_required_fields) && step.snap_required_fields.length
      ? step.snap_required_fields
      : [{
          // Defensive fallback for legacy step instances the migration
          // hasn't touched yet.
          id: step.id + "::legacy",
          label: step.label || "Champ requis",
          type: step.snap_required_field_type || "text",
          options: step.snap_required_field_options || [],
          active: !!step.snap_required_field_active,
        }];
  }, [step]);

  const storedValues = step.field_values || {};
  const storedFilenames = step.field_filenames || {};

  const [fieldValues, setFieldValues] = useState(() => {
    const out = {};
    for (const f of snapFields) {
      if (f.type !== "file") out[f.id] = storedValues[f.id] || "";
    }
    return out;
  });
  const [stagedFiles, setStagedFiles] = useState({});   // {field_id: File}
  const [stagedFilenames, setStagedFilenames] = useState({}); // {field_id: string}

  // Spec iter152 (Feb 2026) — BUGFIX perte de saisie après upload fichier.
  // On garde en mémoire la DERNIÈRE valeur serveur observée pour chaque
  // champ non-fichier ; à chaque re-render du parent (ex. document
  // rafraîchi après upload), on ne réécrit un brouillon local QUE si le
  // serveur a effectivement changé cette valeur (édition faite ailleurs).
  // Ainsi, la date que l'utilisateur vient de taper reste intacte pendant
  // qu'un fichier voisin s'upload puis se re-rend.
  const lastServerValuesRef = useRef({});

  // Sync from server when the step reloads.
  useEffect(() => {
    setFieldValues((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const f of snapFields) {
        if (f.type === "file") continue;
        const serverVal = storedValues[f.id] || "";
        const lastSeen = lastServerValuesRef.current[f.id];
        if (lastSeen === undefined) {
          // Première synchro pour ce champ (mount initial ou nouveau champ).
          next[f.id] = serverVal;
          changed = true;
        } else if (serverVal !== lastSeen) {
          // Le serveur a bougé depuis la dernière synchro → adopte.
          // (Cas typique : édition simultanée par un autre utilisateur,
          // ou réponse d'un save/close qui persiste la nouvelle valeur.)
          next[f.id] = serverVal;
          changed = true;
        }
        lastServerValuesRef.current[f.id] = serverVal;
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(storedValues)]);
  useEffect(() => setExpanded(isCurrent || isPast), [isCurrent, isPast]);

  const late = step.is_late;
  // Spec rev129 (2026-02-05) — une étape « en cours » qui est aussi
  // « en retard » bascule sur un style ROUGE dominant (au lieu du doré)
  // pour ne pas masquer l'urgence. Palette rouge cohérente avec
  // `#EF4444` (déjà utilisé pour les étapes en retard).
  const isCurrentLate = isCurrent && late;
  // iter185 — étape « À corriger » : réinitialisée par un validateur, orange #E0822E jusqu'à revalidation.
  const isCorrection = !isPast && !step.closed_at && (step.step_status === "a_corriger" || !!step.corrected_at);
  const correctionAuthor = isCorrection ? usersById?.[step.corrected_by] : null;
  const nodeColor = isPast ? "#22C55E" : (isCorrection ? "#E0822E" : (late ? "#EF4444" : (isCurrent ? ACCENT : "#4A4A4A")));
  const inEditMode = !isPast || editing;

  // Spec rev128 (2026-02-05) — style visuel enrichi du stepper vertical.
  //   • Validée   : opacité 0.78, avatar de la personne + coche verte badge,
  //                 numéro préfixé au libellé, "Validée le … · Prénom" à droite.
  //   • En cours  : carte mise en avant (dégradé doré + halo lumineux + badge
  //                 "EN COURS" + numéro dans un badge doré à gauche).
  //   • Future    : neutre (numéro dans un cercle à bordure grise).
  //   • Ligne     : dégradé progressif vert → jaune à l'approche de l'étape
  //                 courante ; grise après.
  const validator = isPast && step.closed_by ? usersById?.[step.closed_by] : null;

  // Couleur de la ligne verticale (rail) reliant l'étape à la suivante.
  //   • Après une étape validée : vert (transition vers l'étape suivante).
  //   • Rail de l'étape « prochaine » (rev140) : gris neutre → gris foncé.
  //   • Rail de l'étape « en retard » : rouge → gris foncé.
  let railBg = "rgba(255,255,255,0.08)";
  if (isPast) {
    railBg = currentIdx === index + 1
      ? (isCurrentLate ? "linear-gradient(180deg, #22C55E 0%, #EF4444 100%)" : "linear-gradient(180deg, #22C55E 0%, #4A4A4A 100%)")
      : (index + 1 < currentIdx || currentIdx === -1)
        ? "#22C55E66"
        : "#22C55E66";
  } else if (isCorrection) {
    railBg = "linear-gradient(180deg, #E0822E 0%, rgba(224,130,46,0.35) 100%)";
  } else if (isCurrent) {
    railBg = isCurrentLate
      ? "linear-gradient(180deg, #EF4444 0%, rgba(74,74,74,0.35) 100%)"
      : "linear-gradient(180deg, #4A4A4A 0%, rgba(74,74,74,0.35) 100%)";
  }

  // Aggregate helpers ------------------------------------------------------
  const activeFields = snapFields.filter((f) => f.active);
  const fieldHasValue = (f) => {
    if (f.type === "file") {
      // Spec 2026-02-XX ext#5 — `storedFilenames[f.id]` peut désormais
      // être un tableau (multi-upload). Un tableau vide compte comme
      // « pas de fichier ».
      const stored = storedFilenames[f.id];
      const hasStored = Array.isArray(stored) ? stored.length > 0 : !!stored;
      return !!(stagedFilenames[f.id] || hasStored);
    }
    if (f.type === "checkbox") return true;
    return !!(fieldValues[f.id] && String(fieldValues[f.id]).trim());
  };
  const allActiveFilled = activeFields.every(fieldHasValue);
  const anyDirty = (() => {
    for (const f of snapFields) {
      if (f.type === "file") {
        if (stagedFiles[f.id]) return true;
      } else {
        const current = String(storedValues[f.id] || "");
        const draft = String(fieldValues[f.id] || "");
        if (current !== draft) return true;
      }
    }
    return false;
  })();

  const canValidate = canEdit && canValidateStep && !saving && (isPast ? anyDirty : allActiveFilled);
  useLeaveGuard({ dirty: anyDirty && inEditMode, label: `Étape « ${step.label || "—"} »`, module: "documents", save: guardedSave(() => validate()), discard: () => { setEditing(false); setStagedFiles({}); setStagedFilenames({}); } }); // eslint-disable-line no-use-before-define

  // Spec 2026-02-XX rev49 — état de progression pour l'overlay
  // d'upload (filename + %). Réinitialisé à chaque fin de transfert.
  const [uploadingInfo, setUploadingInfo] = useState({ filename: "", progress: null });

  // Fresh open-step upload — file gets uploaded IMMEDIATELY, then close_step
  // is optional (creator still needs to click Valider so all required fields
  // are considered).
  const uploadFileForField = async (fieldId, fileObj) => {
    const form = new FormData();
    form.append("file", fileObj);
    form.append("field_id", fieldId);
    setSaving(true);
    setUploadingInfo({ filename: fileObj?.name || "", progress: 0 });
    try {
      const { data } = await api.post(
        `/documents/${docId}/steps/${step.id}/upload-file`,
        form,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            if (!evt.total) return;
            const pct = Math.round((evt.loaded / evt.total) * 100);
            setUploadingInfo((prev) => ({ ...prev, progress: pct }));
          },
        },
      );
      onChanged(data);
      setSavedFlash(true); setTimeout(() => setSavedFlash(false), 1400);
    } catch (err) {
      toast.error("Envoi échoué", { description: err.response?.data?.detail || "" });
    } finally {
      setSaving(false);
      setUploadingInfo({ filename: "", progress: null });
    }
  };

  const onFilePick = (fieldId) => async (ev) => {
    const f = ev.target.files?.[0];
    if (!f) return;
    if (isPast) {
      // Stage locally; will be uploaded on Valider click.
      setStagedFiles((prev) => ({ ...prev, [fieldId]: f }));
      setStagedFilenames((prev) => ({ ...prev, [fieldId]: f.name }));
      return;
    }
    await uploadFileForField(fieldId, f);
  };

  const validate = async () => {
    if (!canValidate) return;
    setSaving(true);
    try {
      let data;
      // Step 1: upload any staged file (closed-step file replacement).
      for (const fid of Object.keys(stagedFiles)) {
        const form = new FormData();
        form.append("file", stagedFiles[fid]);
        form.append("field_id", fid);
        const resp = await api.post(
          `/documents/${docId}/steps/${step.id}/upload-file`,
          form,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        data = resp.data;
      }
      // Step 2: close (or update-in-place) with the value-type fields.
      const valuesPayload = {};
      for (const f of snapFields) {
        if (f.type === "file") continue;
        valuesPayload[f.id] = String(fieldValues[f.id] || "");
      }
      const resp = await api.post(
        `/documents/${docId}/steps/${step.id}/close`,
        { field_values: valuesPayload },
      );
      data = resp.data;
      onChanged(data);
      // Spec rev155 (2026-02-07) — bus d'événements pour rafraîchir la
      // liste Documents (chip "Étapes à revalider") en temps réel :
      // une revalidation d'étape peut faire disparaître un document de
      // ce filtre orange dès l'instant présent.
      window.dispatchEvent(new CustomEvent("ledesk:doc-mutated", {
        detail: { docId, reason: "step-close" },
      }));
      try { localStorage.setItem("ledesk:doc-mutated", String(Date.now())); } catch {}
      setStagedFiles({}); setStagedFilenames({});
      setEditing(false);
      setSavedFlash(true); setTimeout(() => setSavedFlash(false), 1400);
      if (!isPast) toast.success(`Étape « ${step.label} » validée`);
      else toast.success("Étape mise à jour");
    } catch (err) {
      toast.error("Impossible", { description: err.response?.data?.detail || "" });
    } finally {
      setSaving(false);
    }
  };

  // Spec iter220 (2026-02-28) — Le tombstone iter218 est renversé :
  // « Supprimer » réinitialise désormais l'étape à son état vide
  // (voir backend `remove_step_from_document`). L'étape se rend
  // comme n'importe quelle étape normale — pas d'early-return.
  // Traçabilité : si `step.previously_attached_files` est non-vide,
  // un badge discret « X fichier(s) précédemment attaché(s) » est
  // rendu dans le header de la carte (voir bloc plus bas).

  return (
    <div
      className="grid grid-cols-[40px_1fr] gap-3"
      data-testid={`step-row-${index}`}
      data-struck={isStruck || undefined}
      data-locked={stepLocked || undefined}
      style={{ opacity: isStruck ? 0.55 : stepLocked ? 0.6 : isPast ? 0.85 : 1, pointerEvents: isStruck ? "none" : undefined, filter: isStruck ? "grayscale(1)" : stepLocked ? "grayscale(.6)" : undefined }}
    >
      {/* -- Rail vertical : pastille + trait dégradé --------------- */}
      <div className="flex flex-col items-center pt-1">
        {isStruck ? (
          /* Iter123 — étape barrée (document annulé) : carreau gris rayé d'une diagonale. */
          <div className="relative w-8 h-8 rounded-lg flex items-center justify-center text-[12px] font-semibold shrink-0 overflow-hidden"
            style={{ background: "#1f1f22", color: "#6B7280", border: "1.5px dashed #4b4b50" }} data-testid={`step-struck-${index}`} title="Étape barrée — document annulé">
            {index + 1}
            <span aria-hidden="true" style={{ position: "absolute", left: -4, right: -4, top: "50%", height: 1.5, background: "#9CA3AF", transform: "rotate(-45deg)" }} />
          </div>
        ) : isPast ? (
          /* Étape validée — Rosette verte avec le numéro d'étape au
             centre (spec rev132, 2026-02-05). Remplace l'ancien avatar
             + badge coche : la rosette est plus lisible et cohérente
             avec le badge « officiel » (validation actée). */
          <Rosette size={32} color="#22C55E">{index + 1}</Rosette>
        ) : isCorrection ? (
          <div className="flex items-center justify-center text-[13px] font-bold shrink-0" style={{ width: 32, height: 32, borderRadius: 8, background: "#E0822E", color: "#23150a", border: "1.5px solid #f0a35e", boxShadow: "0 0 0 4px rgba(224,130,46,0.18)" }} data-testid={`step-${index}-correction-node`}>
            {index + 1}
          </div>
        ) : isCurrent ? (
          /* Spec rev140 (2026-02-06) — Étape « prochaine » (courante,
             pas encore validée) :
               • Dans les délais → carré arrondi NEUTRE gris (#2A2A2A,
                 bordure #555, texte #CCC). Plus aucun doré/jaune.
               • En retard (isCurrentLate) → carré arrondi ROUGE
                 (dégradé rouge, texte rouge foncé, bordure rouge). */
          <div
            className="flex items-center justify-center text-[13px] font-bold shrink-0"
            style={
              isCurrentLate
                ? {
                    width: 32, height: 32, borderRadius: 8,
                    background: "linear-gradient(135deg, #FCA5A5 0%, #EF4444 100%)",
                    color: "#3F0A0A",
                    border: "1.5px solid rgba(239,68,68,0.55)",
                    boxShadow: "0 0 0 4px rgba(239,68,68,0.20), 0 3px 10px rgba(239,68,68,0.40)",
                  }
                : {
                    width: 32, height: 32, borderRadius: 8,
                    background: "#2A2A2A",
                    color: "#CCCCCC",
                    border: "1.5px solid #555555",
                  }
            }
          >
            {index + 1}
          </div>
        ) : (
          /* Étape future — cercle neutre à bordure grise. */
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-semibold shrink-0"
            style={{
              background: "transparent",
              color: "#6B7280",
              border: "1.5px solid #3A3A3A",
            }}
          >
            {index + 1}
          </div>
        )}
        {!isLast && (
          <div
            className="flex-1 w-0.5 mt-1"
            style={{ background: railBg, minHeight: 28, borderRadius: 1 }}
          />
        )}
      </div>

      {/* -- Colonne contenu : carte + chips fichiers en dessous ------- */}
      <div className="min-w-0">
      {/* -- Carte étape --------------------------------------------- */}
      <div
        className={`rounded-lg pb-2 pt-2.5 px-3.5 transition-all duration-700 ${expanded ? "" : "hover:brightness-110 cursor-pointer"}`}
        style={{
          // Spec rev138 (2026-02-06) — fond neutre uniforme (#161616) pour
          // toutes les cartes d'étapes, quel que soit l'état. Seule
          // l'icône/badge à gauche garde sa couleur.
          background: "#161616",
          // Spec rev151 (2026-02-06) — Liseré orange temporaire ~2.6s
          // sur l'étape rouverte via /corriger. Halo + bordure orange
          // qui s'estompent grâce à `transition-all` (700ms).
          border: justCorrected ? "2px solid #F97316" : "1px solid #2A2A2A",
          boxShadow: justCorrected
            ? "0 0 0 4px rgba(249,115,22,0.20), 0 0 24px rgba(249,115,22,0.35)"
            : "none",
        }}
        onClick={() => !expanded && setExpanded(true)}
        data-testid={`step-card-${index}${isCorrection ? "-correction" : isCurrentLate ? "-current-late" : isCurrent ? "-current" : isPast ? "-past" : "-future"}`}
      >
        {/* -- Header row ------------------------------------------ */}
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="font-semibold text-[13.5px]"
            style={{ color: isStruck ? "#6B7280" : "#EDEDED", textDecoration: isStruck ? "line-through" : undefined }}
          >
            {/* Spec rev132 (2026-02-05) — le numéro d'étape est déjà
                affiché dans la rosette / badge à gauche : on ne le
                répète PAS dans le libellé. */}
            {step.label}
          </span>
          {adhoc && <AddedChip testId={`step-${index}-added-chip`} />}
          {adhoc && onRemoveAdhoc && (
            <button type="button" onClick={(e) => { e.stopPropagation(); onRemoveAdhoc(); }} title="Retirer cette étape ajoutée" className="inline-flex items-center justify-center hover:text-red-400" style={{ width: 18, height: 18, color: "#71717c", background: "transparent", border: "none", cursor: "pointer" }} data-testid={`step-${index}-remove-adhoc`}><XCircle size={12} /></button>
          )}
          {step.reopened_auto_at && !step.closed_at && (
            <span
              className="inline-flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-widest rounded-full px-2 py-0.5"
              style={{ background: "rgba(249,168,38,.16)", color: "#F9A826", border: "0.5px solid rgba(249,168,38,.5)", cursor: "help" }}
              title={`Réouverte automatiquement le ${new Date(step.reopened_auto_at).toLocaleString("fr-FR", { timeZone: "Africa/Algiers" })} — champ obligatoire manquant : ${(step.reopened_auto_fields || []).join(", ")}. Motif : ${step.reopened_auto_reason || "—"}`}
              data-testid={`step-${index}-reopened-badge`}
            >
              <AlertTriangle size={9} strokeWidth={2.6} /> Réouverte
            </span>
          )}
          {isCorrection && (
            <span className="inline-flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-widest rounded-full px-2 py-0.5" style={{ background: "#23150a", border: "1px solid #6b3f14", color: "#f0a35e" }} data-testid={`step-${index}-correction-badge`}>
              <AlertTriangle size={9} strokeWidth={2.6} /> À corriger
            </span>
          )}
          {isCurrent && !isCorrection && (
            <span
              className="inline-flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-widest rounded-full px-2 py-0.5"
              style={
                isCurrentLate
                  ? {
                      background: "linear-gradient(135deg, #FCA5A5 0%, #EF4444 100%)",
                      color: "#3F0A0A",
                      boxShadow: "0 1px 3px rgba(239,68,68,0.45)",
                    }
                  : {
                      // Spec rev140 (2026-02-06) — badge neutre gris pour
                      // l'étape « prochaine » dans les délais. Plus de doré.
                      background: "#2A2A2A",
                      color: "#AAAAAA",
                      border: "1px solid #3A3A3A",
                    }
              }
              data-testid={`step-${index}-current-badge`}
            >
              {isCurrentLate ? (
                <>
                  <AlertTriangle size={9} strokeWidth={2.6} /> En retard
                </>
              ) : (
                "Prochaine"
              )}
            </span>
          )}
          {/* Spec iter118a (Feb 2026) — LISTE de responsables. On rend
              un badge cliquable par responsable (fallback créateur si
              la liste est vide). Chaque badge deep-linke vers
              Paramètres > Utilisateurs (highlight sur l'uid). */}
          {stepLocked && !isStruck && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold rounded px-1.5 py-0.5 shrink-0"
              style={{ background: "rgba(245,200,66,.12)", border: "1px solid rgba(245,200,66,.45)", color: "#F5C842" }}
              title={restrictedTitle} data-testid={`step-${index}-reserved-chip`}>
              <Lock size={10} /> Réservé à
              <span className="inline-flex items-center -space-x-1 ml-0.5">
                {responsibleIds.map((id) => usersById?.[id]).filter(Boolean).map((u) => (
                  <UserAvatar key={u.id} user={u} size={18} />
                ))}
              </span>
            </span>
          )}

          {isCurrent && !stepLocked && (() => {
            const list = Array.isArray(step.snap_responsible_user_ids)
              ? step.snap_responsible_user_ids
              : (step.snap_responsible_user_id ? [step.snap_responsible_user_id] : []);
            if (list.length === 0) {
              // Fallback créateur — badge unique gris.
              const name = "Créateur";
              const initials = "?";
              const handleClick = (e) => {
                e.stopPropagation();
                navigate(`/parametres?tab=users`);
              };
              return (
                <button
                  type="button"
                  onClick={handleClick}
                  className="inline-flex items-center gap-1.5 text-[10px] rounded-full pl-0.5 pr-2 py-0.5 cursor-pointer transition-all hover:brightness-125 hover:scale-[1.02]"
                  style={{
                    background: "#1B1B1B",
                    border: `1px solid #3A3A3A`,
                    color: "#EFEFEF",
                  }}
                  title="Aucun responsable défini — fallback créateur (ouvrir Paramètres > Utilisateurs)"
                  data-testid={`step-${index}-responsible-badge`}
                >
                  <span
                    className="inline-flex items-center justify-center rounded-full text-[9px] font-bold"
                    style={{ width: 18, height: 18, background: "#3A3A3A", color: "#FFFFFF" }}
                  >
                    {initials}
                  </span>
                  <span className="truncate max-w-[140px]">{name}</span>
                </button>
              );
            }
            return list.map((respUid, ri) => {
              const u = usersById ? usersById[respUid] : null;
              const name = u ? (`${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email) : (respUid || "?");
              const initials = (name || "?").split(/\s+/).map(p => p[0]).slice(0, 2).join("").toUpperCase() || "?";
              const handleClick = (e) => {
                e.stopPropagation();
                const qs = `?tab=users&highlight=${encodeURIComponent(respUid)}`;
                navigate(`/parametres${qs}`);
              };
              return (
                <button
                  key={respUid}
                  type="button"
                  onClick={handleClick}
                  className="inline-flex items-center gap-1.5 text-[10px] rounded-full pl-0.5 pr-2 py-0.5 cursor-pointer transition-all hover:brightness-125 hover:scale-[1.02]"
                  style={{
                    background: "#1B1B1B",
                    border: `1px solid #5b5fc7`,
                    color: "#EFEFEF",
                  }}
                  title={`Responsable de l'étape : ${name} — cliquer pour ouvrir Paramètres > Utilisateurs`}
                  data-testid={ri === 0 ? `step-${index}-responsible-badge` : `step-${index}-responsible-badge-${ri}`}
                >
                  <span
                    className="inline-flex items-center justify-center rounded-full text-[9px] font-bold"
                    style={{ width: 18, height: 18, background: "#5b5fc7", color: "#FFFFFF" }}
                  >
                    {initials}
                  </span>
                  <span className="truncate max-w-[140px]">{name}</span>
                </button>
              );
            });
          })()}
          {late && !isPast && !isCurrent && (
            <Badge variant="outline" className="border-red-500/50 text-red-400 text-[10px]">
              <AlertTriangle size={10} className="mr-1" /> En retard
            </Badge>
          )}
          <div className="flex-1" />
          {/* Spec iter220 (2026-02-28) — Badge discret « X fichier(s)
              précédemment attaché(s) » sur les étapes réinitialisées
              après une suppression. Cliquable → ouvre l'onglet Historique. */}
          {Array.isArray(step.previously_attached_files) && step.previously_attached_files.length > 0 && (() => {
            const totalFiles = step.previously_attached_files.reduce(
              (acc, entry) => acc + (Array.isArray(entry?.files) ? entry.files.length : 0),
              0,
            );
            if (totalFiles === 0) return null;
            return (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  window.dispatchEvent(new CustomEvent("doc-detail-tab", { detail: { tab: "history" } }));
                }}
                className="inline-flex items-center gap-1 text-[10px] font-medium rounded-full px-2 py-0.5 transition-all hover:brightness-125"
                style={{
                  background: "rgba(156,163,175,0.10)",
                  color: "#9CA3AF",
                  border: "1px dashed rgba(156,163,175,0.35)",
                }}
                data-testid={`step-${index}-previously-attached`}
                title={`${totalFiles} fichier(s) précédemment attaché(s) puis effacé(s) par une réinitialisation — voir l'onglet Historique pour le motif et l'auteur.`}
              >
                <Trash2 size={10} strokeWidth={2} />
                {totalFiles} fichier{totalFiles > 1 ? "s" : ""} précédemment attaché{totalFiles > 1 ? "s" : ""} — voir historique
              </button>
            );
          })()}
          {saving && (
            <span className="text-[10px] text-muted-foreground italic" data-testid={`step-${index}-saving`}>
              Enregistrement…
            </span>
          )}
          {savedFlash && !saving && (
            <span className="text-[10px] text-green-400 font-medium" data-testid={`step-${index}-saved`}>
              ✓ Enregistré
            </span>
          )}
          {isCurrent && step.deadline && (
            <span
              className="inline-flex items-center gap-1 text-[10.5px] px-2 py-0.5 rounded-full"
              style={
                isCurrentLate
                  ? { background: "rgba(239,68,68,0.12)", color: "#FCA5A5", border: "1px solid rgba(239,68,68,0.30)" }
                  : { background: "rgba(255,255,255,0.04)", color: "#AAAAAA", border: "1px solid #3A3A3A" }
              }
            >
              <CalendarIcon size={10} strokeWidth={2.2} /> Échéance {fmtDate(step.deadline)}
            </span>
          )}
          {!isCurrent && !isPast && step.deadline && (
            <span className="text-[10px] text-muted-foreground flex items-center gap-1">
              <Clock size={10} /> Échéance : {fmtDate(step.deadline)}
            </span>
          )}
          {/* Spec rev138 (2026-02-06) — Suppression de la ligne « ✓ Validée
              le … · Prénom » ici : redondant avec l'avatar + date sous
              le bouton « Modifier ». Voir bloc plus bas. */}
        </div>
        {isCorrection && (
          <div className="mt-2.5" style={{ borderLeft: "3px solid #E0822E", background: "#23150a", borderRadius: "0 8px 8px 0", padding: "8px 12px" }} data-testid={`step-${index}-correction-banner`}>
            <div className="flex items-center gap-2" style={{ fontSize: 10.5, color: "#f0a35e", fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase" }}>
              <AlertTriangle size={11} strokeWidth={2.4} /> Correction demandée
              <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, color: "#d9a06a" }}>
                par {correctionAuthor ? `${correctionAuthor.first_name} ${correctionAuthor.last_name}` : "un validateur"}{step.corrected_at ? ` · ${new Date(step.corrected_at).toLocaleString("fr-FR", { timeZone: "Africa/Algiers", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}` : ""}
              </span>
            </div>
            <div style={{ fontSize: 12.5, color: "#f3e3d3", marginTop: 4, whiteSpace: "pre-wrap" }} data-testid={`step-${index}-correction-reason`}>{step.corrected_reason || "—"}</div>
            <div style={{ fontSize: 10.5, color: "#b58a63", marginTop: 4 }}>L'étape a été réinitialisée : complétez-la à nouveau puis cliquez sur « Revalider l'étape ». Les anciens fichiers restent consultables dans l'Historique.</div>
          </div>
        )}

        {/* -- Expanded body --------------------------------------- */}
        {expanded && (
          <div className="mt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
            {canEdit && !stepLocked ? (
              <>
                {isPast && !inEditMode ? (
                  // Read-only summary of every field + Modifier button.
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="flex-1 min-w-0 space-y-2">
                      {snapFields.map((f) => (
                        <PastFieldRow
                          key={f.id}
                          field={f}
                          value={storedValues[f.id]}
                          filename={storedFilenames[f.id]}
                          docId={docId}
                          stepId={step.id}
                          canEdit={false}
                        />
                      ))}
                      {step.value_updated_at && (
                        <div className="text-[10px] text-muted-foreground italic" data-testid={`step-${index}-value-updated`}>
                          Valeur(s) modifiée(s) le {fmtDateTime(step.value_updated_at)}
                          {step.value_updated_by && usersById[step.value_updated_by] && (
                            <> · {usersById[step.value_updated_by].first_name || "—"}</>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => setEditing(true)}
                        disabled={!canValidateStep}
                        className="inline-flex items-center justify-center gap-1.5 h-8 w-[118px] px-3 rounded-md text-xs font-medium leading-none box-border transition-colors"
                        style={canValidateStep ? {
                          background: "rgba(90,158,224,0.10)",
                          border: "1px solid rgba(90,158,224,0.30)",
                          color: "#5A9EE0",
                        } : { background: "#2A2A2A", border: "1px solid #3A3A3F", color: "#6B7280", cursor: "not-allowed" }}
                        data-testid={`step-${index}-edit`}
                        title={canValidateStep ? "Modifier les valeurs enregistrées" : restrictedTitle}
                      >
                        <Pencil size={12} strokeWidth={2} /> Modifier
                      </button>
                      {/* Spec rev149 (2026-02-06) — bouton « Corriger »
                          empilé sous « Modifier », même taille (h-8 px-3
                          text-xs), couleur orange (contour #F97316, fond
                          #2A1F14, texte #FB923C). Ouvre la modale
                          d'explication obligatoire (event
                          `doc-corriger-open` avec scope step). */}
                      <button
                        type="button"
                        onClick={() => window.dispatchEvent(new CustomEvent("doc-corriger-open", {
                          detail: { scope: "step", stepIndex: index, stepLabel: step.label }
                        }))}
                        className="inline-flex items-center justify-center gap-1.5 h-8 w-[118px] px-3 rounded-md text-xs font-medium leading-none box-border transition-colors"
                        style={{
                          background: "#2A1F14",
                          border: "1px solid #F97316",
                          color: "#FB923C",
                        }}
                        data-testid={`step-${index}-corriger`}
                        title="Corriger cette étape (explication requise)"
                      >
                        <AlertTriangle size={12} strokeWidth={2} /> Corriger
                      </button>
                      {/* Spec rev138 (2026-02-06) — sous le bouton Modifier :
                          avatar 22px (initiales, seed color) + date & heure
                          brute (ex. « 05 août 26, 13:41 »), vert clair
                          #4ADE80, alignés à droite. SANS le mot « Validée
                          le » ni le nom en toutes lettres. */}
                      {step.closed_at && (() => {
                        const d = new Date(step.closed_at);
                        const dd = String(d.getDate()).padStart(2, "0");
                        const mo = d.toLocaleString("fr-FR", { month: "short" }).replace(".", "");
                        const yy = String(d.getFullYear()).slice(-2);
                        const hh = String(d.getHours()).padStart(2, "0");
                        const mm = String(d.getMinutes()).padStart(2, "0");
                        const stamp = `${dd} ${mo} ${yy}, ${hh}:${mm}`;
                        return (
                        <div className="flex items-center gap-1.5" style={{ color: "#4ADE80" }} data-testid={`step-${index}-validation-meta`}>
                          {validator && (
                            <span className="inline-flex rounded-full shrink-0" title={`${validator.first_name || ""} ${validator.last_name || ""}`.trim() || validator.email}>
                              <UserAvatar user={validator} size={22} showPopover={false} variant="initials" />
                            </span>
                          )}
                          <span className="text-[10.5px] tabular-nums whitespace-nowrap">
                            {stamp}
                          </span>
                        </div>
                        );
                      })()}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                      {snapFields.length > 1
                        ? `${snapFields.length} champs à renseigner`
                        : (activeFields.length > 0 ? "Champ requis" : "Champ optionnel")}
                    </div>
                    <div className="space-y-2">
                      {snapFields.map((f) => (
                        <FieldInputRow
                          key={f.id}
                          field={f}
                          value={fieldValues[f.id]}
                          onChange={(v) => setFieldValues((prev) => ({ ...prev, [f.id]: v }))}
                          stagedFilename={stagedFilenames[f.id]}
                          storedFilename={storedFilenames[f.id]}
                          storedUrl={storedValues[f.id]}
                          onFilePick={onFilePick(f.id)}
                          docId={docId}
                          typeId={typeId}
                          stepId={step.id}
                          step={step}
                          stepIndex={index}
                          docReference={docReference}
                          onChanged={onChanged}
                          saving={saving}
                          uploadingInfo={uploadingInfo}
                          testid={`step-${index}-rf-${f.id}`}
                        />
                      ))}
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                      <button
                        type="button"
                        onClick={validate}
                        disabled={!canValidate}
                        className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md text-xs font-semibold border-0 transition-all"
                        style={{
                          background: canValidate ? (isCorrection ? "#2A9A7A" : "#22C55E") : "#2A2A2A",
                          color: canValidate ? (isCorrection ? "#062019" : "#fff") : "#6B7280",
                          boxShadow: canValidate ? (isCorrection ? "0 2px 8px rgba(42,154,122,0.35)" : "0 2px 8px rgba(34,197,94,0.35)") : "none",
                          cursor: canValidate ? "pointer" : "not-allowed",
                        }}
                        data-testid={`step-${index}-validate`}
                        data-mode={isCorrection ? "revalidate" : "validate"}
                        title={!canValidateStep
                          ? restrictedTitle
                          : canValidate
                          ? (isCorrection ? "Revalider l'étape" : "Valider l'étape")
                          : (isPast ? "Modifiez au moins une valeur pour pouvoir valider" : "Remplissez tous les champs actifs pour pouvoir valider")}
                      >
                        <CheckCircle2 size={14} /> {isCorrection ? "Revalider l'étape" : "Valider l'étape"}
                      </button>
                      {!canValidateStep && (
                        <span className="text-[11px]" style={{ color: "#F5C842" }} data-testid={`step-${index}-restricted-hint`}>
                          {restrictedTitle}
                        </span>
                      )}
                      {isPast && (
                        <button
                          type="button"
                          onClick={() => {
                            setEditing(false);
                            const out = {};
                            for (const f of snapFields) {
                              if (f.type !== "file") out[f.id] = storedValues[f.id] || "";
                            }
                            setFieldValues(out);
                            setStagedFiles({}); setStagedFilenames({});
                          }}
                          // Spec rev156 (2026-02-07) — bouton "Annuler"
                          // sur la carte de l'étape « prochaine » (édition
                          // d'une étape validée). Ancien style : texte
                          // gris foncé quasi illisible sur fond sombre.
                          // Nouveau : fond rouge sombre, bord rouge vif,
                          // texte rouge clair, mêmes dimensions que le
                          // bouton "Valider l'étape" adjacent (h-9 px-4).
                          className="inline-flex items-center h-9 px-4 rounded-md text-xs font-semibold transition-colors"
                          style={{
                            background: "#3A1414",
                            border: "1px solid #EF4444",
                            color: "#F87171",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#4A1A1A";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "#3A1414";
                          }}
                          data-testid={`step-${index}-edit-cancel`}
                        >
                          Annuler
                        </button>
                      )}
                    </div>
                  </>
                )}
              </>
            ) : stepLocked && canEdit ? (
              <div className="space-y-2" data-testid={`step-${index}-locked`}>
                <div className="flex items-center justify-end gap-2 flex-wrap">
                  {isPast && canCorriger && (
                    <button
                      type="button"
                      onClick={() => window.dispatchEvent(new CustomEvent("doc-corriger-open", { detail: { scope: "step", stepIndex: index, stepLabel: step.label } }))}
                      className="inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium leading-none"
                      style={{ background: "#2A1F14", border: "1px solid #F97316", color: "#FB923C" }}
                      data-testid={`step-${index}-corriger`}
                      title="Corriger cette étape (explication requise)"
                    >
                      <AlertTriangle size={12} strokeWidth={2} /> Corriger
                    </button>
                  )}
                </div>
                {snapFields.map((f) => (
                  <PastFieldRow key={f.id} field={f} value={storedValues[f.id]} filename={storedFilenames[f.id]} />
                ))}
              </div>
            ) : isPast ? (
              <div className="space-y-2">
                {snapFields.map((f) => (
                  <PastFieldRow
                    key={f.id}
                    field={f}
                    value={storedValues[f.id]}
                    filename={storedFilenames[f.id]}
                  />
                ))}
              </div>
            ) : null}
          </div>
        )}
      </div>
      {/* -- Chips fichiers SOUS la carte étape (spec rev132 → rev133) --
          Rev133 : si l'étape a >= 2 pièces jointes, on affiche un badge
          groupé cliquable ("N pièces jointes" + chevron). 1 fichier =
          chip directe, 0 = rien. Voir composant `FileChipsGroup`.
          Masqué quand la carte est expanded pour éviter la duplication
          avec les FieldInputRow / PastFieldRow qui listent les fichiers
          à l'intérieur (via leurs propres FileActionChip).
      */}
      {!expanded && (() => {
        const files = snapFields
          .filter((f) => f.type === "file" && storedFilenames[f.id])
          .flatMap((f) => {
            const raw = storedFilenames[f.id];
            const url = storedValues[f.id];
            const list = Array.isArray(raw)
              ? raw.map((fn, i) => ({ filename: fn, url: Array.isArray(url) ? url[i] : url, key: `${f.id}-${i}` }))
              : [{ filename: raw, url, key: `${f.id}-0` }];
            return list;
          })
          .filter((it) => !!it.filename);
        if (files.length === 0) return null;
        return (
          <div className="mt-2">
            <FileChipsGroup
              files={files}
              onView={(f) => openAuthenticatedUrl(f.url, { filename: f.filename })}
              onDownload={(f) => buildDownloadHandler(f.url, { filename: f.filename })()}
              testid={`step-${index}-files`}
            />
          </div>
        );
      })()}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// FieldInputRow — one editable required-field row inside StepRow.
// ---------------------------------------------------------------------------

/**
 * CompactUploadSquare — carré 96×96 « Glisser ou cliquer » rendu à côté
 * des chips quand au moins un fichier est déjà présent. Gère un état
 * local `dragOver` pour un highlight visuel (fond plus clair + bord
 * solide #2B579A) pendant qu'un fichier est survolé au-dessus. Le drop
 * effectif est capté par le `FileDropTarget` englobant (source de
 * vérité). Ici on ne fait que du feedback visuel.
 * Spec rev163 (2026-02) — dimensions initiales 72×72.
 * Spec rev165 (2026-02) — passage à 96×96, icône 22 px, texte 12 px,
 * border-radius 8, pour un CTA d'ajout plus lisible dans la carte
 * d'étape.
 */
function CompactUploadSquare({ onFilePick, testid }) {
  const [dragOver, setDragOver] = useState(false);
  const stop = (e) => { e.preventDefault(); };
  return (
    <label
      className="cursor-pointer flex flex-col items-center justify-center text-center shrink-0"
      style={{
        background: dragOver ? "#152C42" : "#0F1E2E",
        border: dragOver ? "1.5px solid #2B579A" : "1.5px dashed #0078D4",
        width: 96,
        height: 96,
        borderRadius: 8,
        padding: 8,
        gap: 6,
        transition: "background-color 180ms ease, border-color 180ms ease",
      }}
      title="Cliquer ou glisser-déposer un fichier"
      data-testid={`${testid}-add-square`}
      onDragEnter={(e) => { stop(e); setDragOver(true); }}
      onDragOver={(e) => { stop(e); if (!dragOver) setDragOver(true); }}
      onDragLeave={(e) => { stop(e); setDragOver(false); }}
      onDrop={() => setDragOver(false)}
    >
      <Upload size={22} strokeWidth={2} style={{ color: "#5B9EE8" }} />
      <span
        className="font-semibold leading-tight"
        style={{ color: "#5B9EE8", fontSize: 12 }}
      >
        Glisser
        <br />
        ou cliquer
      </span>
      <input type="file" onChange={onFilePick} className="hidden" data-testid={testid} />
    </label>
  );
}

// Spec iter155 (2026-02-25) — helper remonté au-dessus de ``FieldInputRow``
// / ``PastFieldRow`` / ``PastArtefacts`` qui l'utilisent, pour satisfaire
// ``no-use-before-define`` (garde-fou TDZ activé sur les pages critiques).
const _typeLabel = (t) =>
  t === "text" ? "Texte"
  : t === "number" ? "Nombre"
  : t === "date" ? "Date"
  : t === "money" ? "Montant"
  : t === "dropdown" ? "Choix"
  : t === "checkbox" ? "Case à cocher"
  : t === "file" ? "Fichier"
  : t;

function FieldInputRow({ field, value, onChange, stagedFilename, storedFilename, storedUrl, onFilePick, testid, docId, stepId, step = null, stepIndex = null, docReference = "", onChanged, saving = false, uploadingInfo = null, typeId }) {
  // Spec 2026-02-XX rev47 — Feedback pop après upload réussi : on marque
  // les fichiers récemment ajoutés pour leur appliquer une animation
  // `scale + fade` de 0.35 s. Chaque nouveau fichier est repéré par comparaison
  // de la liste précédente avec la liste courante des fichiers stockés.
  const prevKeysRef = useRef(null);
  const [poppedKeys, setPoppedKeys] = useState(() => new Set());
  const showRequiredStar = !!field.active;
  const { openAuthenticatedUrl, buildDownloadHandler } = useFilePreview();

  // Spec 2026-02-XX ext#5 — le champ Fichier d'étape supporte le
  // multi-upload. Normalisation : legacy scalaire → [scalaire] ; array → array.
  // Spec iter156 — enveloppé dans useMemo pour stabiliser la référence
  // et satisfaire react-hooks/exhaustive-deps sur le useEffect ci-dessous.
  const storedList = useMemo(() => (
    Array.isArray(storedFilename)
      ? storedFilename.map((fn, i) => ({
          filename: fn,
          url: Array.isArray(storedUrl) ? storedUrl[i] : storedUrl,
          idx: i,
        }))
      : (storedFilename ? [{ filename: storedFilename, url: storedUrl, idx: 0 }] : [])
  ), [storedFilename, storedUrl]);

  const hasStored = storedList.length > 0;

  // Spec 2026-02-XX rev47 — Détection des fichiers fraîchement ajoutés :
  // on compare les clés `filename+idx` de la liste courante à celles
  // du rendu précédent. Un fichier absent du rendu précédent est
  // considéré comme "nouveau" et reçoit l'animation `pop`. Les items
  // sont retirés du set 400 ms plus tard (durée de l'animation) via un
  // timeout. Au tout premier rendu (`prevKeysRef.current === null`),
  // aucune animation n'est jouée pour éviter le pop parasite au load.
  useEffect(() => {
    const keys = new Set(storedList.map((it) => `${it.idx}:${it.filename}`));
    const prev = prevKeysRef.current;
    if (prev === null) {
      prevKeysRef.current = keys;
      return;
    }
    const fresh = [...keys].filter((k) => !prev.has(k));
    prevKeysRef.current = keys;
    if (fresh.length === 0) return;
    setPoppedKeys((s) => {
      const next = new Set(s);
      fresh.forEach((k) => next.add(k));
      return next;
    });
    const t = setTimeout(() => {
      setPoppedKeys((s) => {
        const next = new Set(s);
        fresh.forEach((k) => next.delete(k));
        return next;
      });
    }, 450);
    return () => clearTimeout(t);
  // Spec iter156 — ``storedList`` est stabilisé via useMemo ci-dessus,
  // sa référence ne change que quand le contenu change réellement.
  }, [storedList]);

  // Spec iter208bis (2026-02-28) — La confirmation de suppression est
  // désormais servie par un dialogue Shadcn stylé (ConfirmDeleteFileDialog)
  // à la place de `window.confirm` (non-thématique et mal positionné).
  //
  // Correctif bug « Index de fichier invalide » (iter208bis) :
  //   • On stocke l'ITEM complet (filename + idx serveur) au lieu du seul
  //     `idx` dérivé de la position de la liste locale — évite tout
  //     décalage si la liste a été rechargée entre le clic et l'appel.
  //   • Après suppression, on `await load()` pour re-synchroniser la vue
  //     avec l'état backend (au lieu de se fier uniquement au payload
  //     retourné, qui peut arriver après une autre requête).
  const [deleteTarget, setDeleteTarget] = useState(null); // { filename, idx }
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Intégrité : supprimer le DERNIER fichier d'un champ obligatoire sur une
  // étape VALIDÉE invalide l'étape → modale dédiée avec motif (≥ 5 car.).
  const invalidatesStep = !!step?.closed_at && field.active !== false && storedList.length <= 1;
  const requestDelete = (item) => {
    if (!item || typeof item.idx !== "number") return;
    setDeleteTarget({ filename: item.filename, idx: item.idx, invalidates: invalidatesStep });
  };
  const performDelete = async (reason) => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      // Spec iter209ter (2026-02-28) — envoie AUSSI le filename : le
      // backend priorise la résolution par nom, tolérant à toute
      // désynchronisation d'index (upload/delete concurrent, SSE, etc.).
      const { data } = await api.delete(
        `/documents/${docId}/steps/${stepId}/file?field_id=${encodeURIComponent(field.id)}&idx=${deleteTarget.idx}&filename=${encodeURIComponent(deleteTarget.filename || "")}${reason ? `&reason=${encodeURIComponent(reason)}` : ""}`,
      );
      onChanged?.(data);
      toast.success(deleteTarget.invalidates ? "Fichier supprimé — étape repassée en « À corriger »" : "Fichier supprimé");
      setDeleteTarget(null);
    } catch (err) {
      toast.error("Suppression impossible", { description: err.response?.data?.detail || "" });
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div className="rounded-md p-2" style={{ background: "#161616", border: "1px solid #2A2A2A" }}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
        {field.label || "Champ"}
        {showRequiredStar && <span className="text-red-500 ml-1">*</span>}
        <span className="opacity-70 normal-case tracking-normal ml-2">— {_typeLabel(field.type)}</span>
        {!field.active && (
          <span className="text-[9px] normal-case ml-2 opacity-60">(optionnel)</span>
        )}
      </div>
      {field.type === "file" ? (
        <FileDropTarget onFilePicked={(f) => onFilePick({ target: { files: [f] } })}
          className="w-full" data-testid={`${testid}-drop`} uploading={saving}
          uploadingInfo={uploadingInfo} accent="#2B579A">
          <div className="space-y-1">
            {/* Spec 2026-02-XX rev47 — Keyframe locale pour l'animation
                d'apparition des nouveaux fichiers uploadés (pop + fade). */}
            <style>{`
              @keyframes lb-file-pop {
                0%   { transform: scale(0.35); opacity: 0; }
                60%  { transform: scale(1.10); opacity: 1; }
                80%  { transform: scale(0.98); }
                100% { transform: scale(1);    opacity: 1; }
              }
              .lb-file-pop { animation: lb-file-pop 0.40s cubic-bezier(0.34, 1.56, 0.64, 1) both; }
            `}</style>
            {/* Spec rev161 (2026-02) — quand au moins un fichier est déjà
                présent (stocké ou staged), on regroupe les chips et un
                CARRÉ compact d'upload (~72×72) dans un même flex-wrap :
                l'upload d'un fichier supplémentaire se fait « à côté »
                des chips existants, non plus en pleine largeur en
                dessous. Sans fichier, on garde la bande primaire en
                pleine largeur (CTA principal). */}
            {hasStored || stagedFilename ? (
              <div className="flex flex-wrap items-start gap-2">
                {hasStored && storedList.map((item) => {
                  const popKey = `${item.idx}:${item.filename}`;
                  const isNew = poppedKeys.has(popKey);
                  return (
                    <div
                      key={`stored-${item.idx}`}
                      className={`flex ${isNew ? "lb-file-pop" : ""}`}
                    >
                      <FileActionChip
                        filename={item.filename}
                        url={item.url}
                        onView={() => openAuthenticatedUrl(item.url, { filename: item.filename })}
                        onDownload={buildDownloadHandler(item.url, { filename: item.filename })}
                        onDelete={() => requestDelete(item)}
                        testid={`${testid}-chip-${item.idx}`}
                      />
                    </div>
                  );
                })}
                {stagedFilename && (
                  <div className="flex items-center gap-2 text-xs flex-wrap">
                    <span className="text-yellow-300 font-medium">⏳ {stagedFilename}</span>
                    <span className="text-[10px] text-muted-foreground italic">— sera uploadé à la validation</span>
                  </div>
                )}
                <CompactUploadSquare onFilePick={onFilePick} testid={testid} />
              </div>
            ) : (
              <label
                className="cursor-pointer flex flex-col items-center justify-center text-center rounded-lg transition-colors w-full"
                style={{
                  background: "#0F1E2E",
                  border: "1.5px dashed #0078D4",
                  minHeight: 92,
                  padding: "16px 12px",
                  gap: 8,
                }}
                title="Cliquer ou glisser-déposer un fichier"
              >
                <Upload size={22} strokeWidth={2} style={{ color: "#5B9EE8" }} />
                <span className="text-[12.5px] font-semibold leading-tight" style={{ color: "#5B9EE8" }}>
                  Glisser-déposer ou cliquer pour charger
                </span>
                <input type="file" onChange={onFilePick} className="hidden" data-testid={testid} />
              </label>
            )}
          </div>
        </FileDropTarget>
      ) : (
        <RequiredFieldInput
          type={field.type}
          value={value}
          onChange={onChange}
          step={{ snap_required_field_options: field.options }}
          testid={testid}
          typeId={typeId}
          fieldId={field.id}
        />
      )}
      {/* Spec iter208bis (2026-02-28) — modale de confirmation stylée
          en lieu et place de window.confirm. */}
      <ConfirmDeleteFileDialog
        open={!!deleteTarget && !deleteTarget.invalidates}
        onOpenChange={(v) => { if (!v && !deleteBusy) setDeleteTarget(null); }}
        filename={deleteTarget?.filename}
        onConfirm={() => performDelete()}
        busy={deleteBusy}
      />
      <DeleteRequiredFileDialog
        open={!!deleteTarget && !!deleteTarget.invalidates}
        filename={deleteTarget?.filename}
        stepIndex={stepIndex}
        stepLabel={step?.label}
        fieldLabel={field.label}
        docReference={docReference}
        busy={deleteBusy}
        onClose={() => !deleteBusy && setDeleteTarget(null)}
        onConfirm={performDelete}
        testId={`${testid}-delete-required`}
      />
    </div>
  );
}


// ---------------------------------------------------------------------------
// PastFieldRow — read-only render of one stored required-field.
// ---------------------------------------------------------------------------
function PastFieldRow({ field, value, filename }) {
  const ftype = field.type;
  const { openAuthenticatedUrl, buildDownloadHandler } = useFilePreview();

  // Spec 2026-02-XX ext#5 — normalise multi-file.
  const fileList = ftype === "file"
    ? (Array.isArray(filename)
        ? filename.map((fn, i) => ({ filename: fn, url: Array.isArray(value) ? value[i] : value, idx: i }))
        : (filename ? [{ filename, url: value, idx: 0 }] : []))
    : [];

  return (
    <div className="rounded-md p-2" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
        {field.label || "Champ"}
        <span className="opacity-70 normal-case tracking-normal ml-2">— {_typeLabel(ftype)}</span>
      </div>
      {ftype === "file" ? (
        fileList.length ? (
          <FileChipsGroup
            files={fileList
              .filter((item) => !!item.url)
              .map((item) => ({ filename: item.filename, url: item.url, key: `past-${item.idx}` }))}
            onView={(f) => openAuthenticatedUrl(f.url, { filename: f.filename })}
            onDownload={(f) => buildDownloadHandler(f.url, { filename: f.filename })()}
            testid={`past-field-${field.id || "f"}-files`}
          />
        ) : (
          <div className="text-xs text-muted-foreground italic">Aucun fichier joint.</div>
        )
      ) : value ? (
        <div className="text-sm whitespace-pre-wrap">
          {ftype === "date" ? fmtDate(value)
            : ftype === "money" ? formatMoney(value)
            : ftype === "number" ? Number(value).toLocaleString("fr-FR")
            : value}
        </div>
      ) : (
        <div className="text-xs text-muted-foreground italic">Vide.</div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// RequiredFieldInput — dispatches based on the step's required_field_type.
// All value types are pure controlled inputs (no onBlur auto-close). File
// type shows either the currently attached artefact or a picker.
// ---------------------------------------------------------------------------
function RequiredFieldInput({ type, value, onChange, step, stagedFileName, onOpenFilePick, testid, uploading = false, typeId, fieldId }) {
  const { openAuthenticatedUrl, buildDownloadHandler } = useFilePreview();
  if (type === "date") {
    return (
      <input type="date" value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 px-2 rounded-md border bg-transparent w-full text-[13px]"
        style={{ borderColor: "#383838", background: "#212121", color: "#EDEDED" }}
        data-testid={testid}
      />
    );
  }
  if (type === "number" || type === "money") {
    if (type === "money") {
      return (
        <MoneyInput
          value={value}
          onChange={onChange}
          testid={testid}
          className="h-9"
          style={{ borderColor: "#383838", background: "#212121", color: "#EDEDED" }}
          placeholder="0"
        />
      );
    }
    return (
      <input
        type="number"
        step="1"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Saisir un nombre…"
        className="h-9 px-2 rounded-md border w-full text-[13px] placeholder:text-[#666]"
        style={{ borderColor: "#383838", background: "#212121", color: "#EDEDED" }}
        data-testid={testid}
      />
    );
  }
  if (type === "dropdown") {
    const opts = step.snap_required_field_options || [];
    return (
      <DSelect
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 px-2 rounded-md border w-full text-[13px]"
        style={{ borderColor: "#383838", background: "#212121", color: "#EDEDED" }}
        data-testid={testid}
      >
        <option value="">Choisir…</option>
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </DSelect>
    );
  }
  if (type === "checkbox") {
    return (
      <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={value === "true" || value === true}
          onChange={(e) => onChange(e.target.checked ? "true" : "false")}
          className="h-4 w-4 accent-[#2B579A]"
          data-testid={testid}
        />
        <span>Coché</span>
      </label>
    );
  }
  if (type === "file") {
    // Two visual states: (a) an artefact is attached (open step post-upload
    // OR closed step with a staged new file) — show the filename + a
    // "Remplacer" picker. (b) nothing attached yet — show a big picker.
    // Spec 2026-02-XX rev31 — le mode "Remplacer" supporte aussi le
    // glisser-déposer (cohérence avec la zone d'upload initiale).
    const displayName = stagedFileName || step.field_filename;
    if (displayName) {
      return (
        <FileDropTarget onFilePicked={(f) => onOpenFilePick({ target: { files: [f] } })} uploading={uploading}>
          <div className="flex items-center gap-2 text-xs flex-wrap">
            <span className="text-green-400 font-medium">✓ {displayName}</span>
            {step.field_value && !stagedFileName && (
              <>
                <button type="button" onClick={() => openAuthenticatedUrl(step.field_value, { filename: step.field_filename })}
                  className="underline bg-transparent border-0 cursor-pointer p-0" style={{ color: ACCENT }}>Voir</button>
                <button type="button" onClick={buildDownloadHandler(step.field_value, { filename: step.field_filename })}
                  className="underline bg-transparent border-0 cursor-pointer p-0 inline-flex items-center gap-1" style={{ color: ACCENT }}>
                  <Download size={11} /> Télécharger
                </button>
              </>
            )}
            <label className="cursor-pointer inline-flex items-center gap-1.5 text-muted-foreground underline"
              title="Cliquer ou glisser-déposer un fichier">
              <Upload size={10} />
              {stagedFileName ? "Choisir un autre fichier" : "Remplacer (clic ou glisser)"}
              <input type="file" onChange={onOpenFilePick} className="hidden" data-testid={`${testid}-replace`} />
            </label>
          </div>
        </FileDropTarget>
      );
    }
    return (
      <FileDropTarget onFilePicked={(f) => onOpenFilePick({ target: { files: [f] } })} uploading={uploading}>
        <label
          className="cursor-pointer flex flex-col items-center justify-center text-center rounded-lg w-full"
          style={{
            background: "#0F1E2E",
            border: "1.5px dashed #0078D4",
            minHeight: 92,
            padding: "16px 12px",
            gap: 8,
          }}
        >
          <Upload size={22} strokeWidth={2} style={{ color: "#5B9EE8" }} />
          <span className="text-[12.5px] font-semibold leading-tight" style={{ color: "#5B9EE8" }}>
            Glisser-déposer ou cliquer pour charger
          </span>
          <input type="file" onChange={onOpenFilePick} className="hidden" data-testid={testid} />
        </label>
      </FileDropTarget>
    );
  }
  // Text — default
  // Spec rev60 (2026-08-02) — autocomplétion basée sur l'historique
  // des valeurs saisies pour ce même champ sur le même type de document.
  // Non-restrictif : l'utilisateur peut valider une valeur inédite.
  return (
    <AutocompleteTextInput
      value={value}
      onChange={onChange}
      typeId={typeId}
      fieldId={fieldId}
      testid={testid}
      placeholder="Saisir la valeur requise…"
      style={{ borderColor: "#383838", background: "#212121", color: "#EDEDED" }}
    />
  );
}


// ---------------------------------------------------------------------------
// PastArtefacts — read-only display for a closed step.
// ---------------------------------------------------------------------------
function PastArtefacts({ step }) {
  // Spec 2026-02-XX rev34 — plus de nouvel onglet : la valeur fichier est
  // ouverte dans la visionneuse intégrée globale (FilePreviewContext),
  // cohérente avec les autres boutons "Voir" de la page.
  const { openAuthenticatedUrl } = useFilePreview();
  if (!step.field_value) {
    return <div className="text-xs text-muted-foreground italic">Étape validée sans valeur.</div>;
  }
  const ftype = step.snap_required_field_type || "text";
  return (
    <div className="text-xs">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">
        {_typeLabel(ftype)} saisi(e)
      </div>
      {ftype === "file" ? (
        <button
          type="button"
          onClick={() => openAuthenticatedUrl(step.field_value, { filename: step.field_filename })}
          className="underline inline-flex items-center gap-1 hover:opacity-80 transition"
          style={{ color: ACCENT, background: "none", border: 0, padding: 0, cursor: "pointer" }}
        >
          <Download size={11} /> {step.field_filename || "Voir"}
        </button>
      ) : (
        <div className="whitespace-pre-wrap">
          {ftype === "date" ? fmtDate(step.field_value)
            : ftype === "money" ? formatMoney(step.field_value)
            : ftype === "number" ? Number(step.field_value).toLocaleString("fr-FR")
            : step.field_value}
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Field value formatter (mirrors the list-view helper).
// ---------------------------------------------------------------------------
function _fmtValue(field, v) {
  if (v === undefined || v === null || v === "") return "—";
  if (field.type === "money") return formatMoney(v);
  if (field.type === "number") return Number(v).toLocaleString("fr-FR");
  if (field.type === "date") return fmtDate(v);
  if (field.type === "checkbox") return v ? "Oui" : "Non";
  return String(v);
}


// ---------------------------------------------------------------------------
// HistoryPanel — audit trail of every change made to the document.
//
// One entry per event: field edit (before → after), file replacement, status
// transition, step close, post-close step value update. Sorted DESC by
// created_at (most recent first).
//
// Backend gates access to creator / manager of creator / admin — anyone else
// gets a 403 which we surface as a friendly notice.
// ---------------------------------------------------------------------------
const EVENT_META = {
  // Spec iter260 (2026-03-01) — chaque event a un badge court en
  // majuscules (« AJOUTÉ », « SUPPRIMÉ », …) affiché en tête de ligne
  // et un `kind` (add / del / mod / validate / info) qui détermine la
  // teinte du carré d'icône.
  field_change:  { Icon: Pencil,       color: "#5A9EE0", label: "Champ modifié",     badge: "MODIFIÉ",  kind: "mod" },
  file_replace:  { Icon: RefreshCw,    color: "#F5C842", label: "Fichier remplacé",   badge: "REMPLACÉ", kind: "mod" },
  status_change: { Icon: Info,         color: "#0078D4", label: "Changement de statut", badge: "STATUT",  kind: "info" },
  step_close:    { Icon: CheckCircle2, color: "#22C55E", label: "Étape validée",      badge: "VALIDÉE",  kind: "validate" },
  step_value:    { Icon: Pencil,       color: "#F97316", label: "Valeur d'étape modifiée", badge: "MODIFIÉ", kind: "mod" },
  step_file:     { Icon: Archive,      color: "#B478E8", label: "Fichier d'étape",    badge: "FICHIER",  kind: "mod" },
  step_added:    { Icon: Plus,         color: "#22C55E", label: "Étape ajoutée",      badge: "AJOUTÉ",   kind: "add" },
  step_removed:  { Icon: Trash2,       color: "#EF4444", label: "Étape retirée",      badge: "SUPPRIMÉ", kind: "del" },
  step_removed_manual: { Icon: Trash2, color: "#B91C1C", label: "Étape supprimée",    badge: "SUPPRIMÉ", kind: "del" },
  corriger:      { Icon: AlertTriangle, color: "#E0822E", label: "Correction",        badge: "À CORRIGER", kind: "mod" },
  step_file_archived: { Icon: Archive, color: "#E0822E", label: "Fichier archivé (correction)", badge: "ARCHIVÉ", kind: "info" },
};

// Spec iter260 — teintes carré d'icône selon le kind d'action.
const _KIND_TINT = {
  add:      { bg: "rgba(34,197,94,0.18)",  fg: "#22C55E" },
  del:      { bg: "rgba(239,68,68,0.18)",  fg: "#EF4444" },
  mod:      { bg: "rgba(249,115,22,0.18)", fg: "#F97316" },
  validate: { bg: "rgba(34,197,94,0.18)",  fg: "#22C55E" },
  info:     { bg: "rgba(90,158,224,0.18)", fg: "#5A9EE0" },
};

// Spec iter260 — détecte si un event concerne un fichier et extrait
// un nom de fichier lisible + une URL propre pour le composant
// FileActionChip. Absorbe le legacy où `after` peut être un chemin API
// brut « /api/documents/{id}/... » (ancien format d'events).
function _fileFromHistoryEvent(e) {
  const raw =
    e.file_name ||
    e.filename ||
    e.after_filename ||
    (typeof e.after === "string" && e.after) ||
    null;
  if (!raw) return null;
  // Ignore les strings non-fichier (dates, "Oui/Non", etc.).
  const looksLikeFile = /\.[a-z0-9]{2,5}$/i.test(raw) || raw.startsWith("/api/") || raw.startsWith("http");
  if (!looksLikeFile) return null;
  // Extrait le nom du fichier si c'est un chemin API brut.
  let filename = raw;
  if (raw.startsWith("/api/") || raw.startsWith("http")) {
    filename = e.file_name || e.filename || "fichier";
  } else if (raw.includes("/")) {
    filename = raw.split("/").pop();
  }
  const url = e.file_url || (raw.startsWith("/api/") ? raw.slice(4) : null);
  return { filename, url };
}

// Spec iter260 — reformate le titre technique en langage naturel.
function _humanTitleForEvent(e, meta) {
  const stepLabel = e.step_label || null;
  const fieldLabel = e.field_label || null;
  const isFileField = e.field_key === "document" || e.field_type === "file";
  if (e.event_type === "step_close" && stepLabel) return `Étape « ${stepLabel} »`;
  if ((e.event_type === "step_added" || e.event_type === "step_removed" || e.event_type === "step_removed_manual") && stepLabel) {
    return `Étape « ${stepLabel} »`;
  }
  if (e.event_type === "file_replace" && stepLabel) return `Fichier sur « ${stepLabel} »`;
  if (e.event_type === "step_file" && stepLabel) return `Fichier sur « ${stepLabel} »`;
  if (e.event_type === "step_file_archived") return `Ancien fichier « ${e.file_name || fieldLabel || "fichier"} » — étape « ${fieldLabel || "—"} »`;
  if (isFileField && stepLabel) return `Fichier sur « ${stepLabel} »`;
  if (isFileField && fieldLabel) return `Fichier « ${fieldLabel} »`;
  if (stepLabel && fieldLabel) return `${fieldLabel} · Étape « ${stepLabel} »`;
  if (stepLabel) return `Étape « ${stepLabel} »`;
  if (fieldLabel) return fieldLabel;
  return meta.label;
}

function _formatValueForHistory(v) {
  if (v === null || v === undefined || v === "") return <span className="italic text-muted-foreground">vide</span>;
  if (typeof v === "boolean") return v ? "Oui" : "Non";
  if (typeof v === "object") return JSON.stringify(v).slice(0, 60);
  return String(v);
}

function HistoryPanel({ docId, doc, usersById, isAdmin = false }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  // Spec iter260 — accès au preview de fichier partagé pour les puces
  // « fichier » dans l'historique.
  const { openAuthenticatedUrl, buildDownloadHandler } = useFilePreview();

  useEffect(() => {
    let stopped = false;
    (async () => {
      try {
        const { data } = await api.get(`/documents/${docId}/history`);
        if (!stopped) setItems(data || []);
      } catch (err) {
        if (!stopped) setError(err.response?.data?.detail || "Accès refusé.");
      }
    })();
    return () => { stopped = true; };
  }, [docId]);

  const _fmtWhen = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  };

  // Spec iter222 point 3 (2026-02-28) — Section « Fichiers précédemment
  // attachés » (snapshot des uploads effacés par une réinitialisation
  // d'étape iter220). Chaque snapshot est affiché en carte compacte
  // avec motif + auteur + date + boutons « Télécharger » (admin only).
  const previousFilesSection = (() => {
    const steps = (doc?.steps) || [];
    const entries = [];
    steps.forEach((step, sIdx) => {
      (step.previously_attached_files || []).forEach((entry, eIdx) => {
        (entry.files || []).forEach((file, fIdx) => {
          entries.push({
            step, sIdx, entry, eIdx, file, fIdx,
            key: `${step.id}-${eIdx}-${fIdx}`,
          });
        });
      });
    });
    if (entries.length === 0) return null;
    return (
      <Card
        className="p-0 bg-card border-vertec-borderDark overflow-hidden mb-4"
        data-testid="doc-history-previous-files"
      >
        <div className="px-5 py-3 border-b border-white/5 text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
          <Trash2 size={12} /> Fichiers précédemment attachés · {entries.length}
        </div>
        <ul className="divide-y divide-white/5">
          {entries.map(({ step, entry, eIdx, file, fIdx, key }) => {
            const actor = entry.by ? usersById?.[entry.by] : null;
            const actorName = actor
              ? `${actor.first_name || ""} ${actor.last_name || ""}`.trim() || actor.email
              : null;
            const dlUrl = `${_V3_API_BASE}/documents/${docId}/steps/${step.id}/previous-files/${eIdx}/${fIdx}/download`;
            return (
              <li
                key={key}
                className="px-5 py-3 flex items-start gap-3"
                data-testid={`previous-file-${key}`}
              >
                <div className="pt-0.5">
                  <Trash2 size={14} style={{ color: "#9CA3AF" }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium truncate" style={{ color: "#E5E7EB" }}>
                    {file.original_filename || "(fichier)"}
                  </div>
                  <div className="text-[11px] mt-0.5" style={{ color: "#9CA3AF" }}>
                    Étape « {step.label} »
                    {actorName && <> · réinitialisée par <span style={{ color: "#D1D5DB" }}>{actorName}</span></>}
                    {entry.at && <> · le {_fmtWhen(entry.at)}</>}
                  </div>
                  {entry.reason && (
                    <div className="text-[11px] italic mt-1" style={{ color: "#9CA3AF" }}>
                      Motif : {entry.reason}
                    </div>
                  )}
                </div>
                {isAdmin && file.storage_key ? (
                  <a
                    href={dlUrl}
                    onClick={buildDownloadHandler(dlUrl, { filename: file.original_filename || file.filename })}
                    className="text-[11px] font-medium px-2.5 py-1 rounded-md transition-all hover:brightness-125 shrink-0"
                    style={{
                      background: "rgba(59,130,246,0.12)",
                      color: "#7fb4ff",
                      border: "1px solid rgba(59,130,246,0.30)",
                    }}
                    data-testid={`previous-file-download-${key}`}
                  >
                    Télécharger
                  </a>
                ) : (
                  <span
                    className="text-[10px] shrink-0"
                    style={{ color: "#6B7280" }}
                    title={file.storage_key ? "Réservé aux administrateurs" : "Emplacement de stockage non conservé (upload antérieur à iter222)"}
                  >
                    {file.storage_key ? "Admin" : "Indispo."}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </Card>
    );
  })();

  if (error) {
    return (
      <Card className="p-5 bg-card border-vertec-borderDark" data-testid="doc-history-forbidden">
        <div className="text-sm text-muted-foreground text-center py-6">{error}</div>
      </Card>
    );
  }
  if (items === null) {
    return (
      <Card className="p-5 bg-card border-vertec-borderDark">
        <div className="text-sm text-muted-foreground text-center py-6">Chargement…</div>
      </Card>
    );
  }
  if (items.length === 0) {
    if (previousFilesSection) {
      return previousFilesSection;
    }
    return (
      <Card className="p-5 bg-card border-vertec-borderDark" data-testid="doc-history-empty">
        <div className="text-sm text-muted-foreground italic text-center py-6">
          Aucune modification enregistrée pour l&apos;instant.
        </div>
      </Card>
    );
  }

  // iter166 — modèle standard d'historique (HistoryTimeline).
  const _badgeFor = (e, meta) => {
    if (e.event_type === "status_change") {
      if (e.after === "cloture") return ["CLÔTURÉ", HISTORY_COLORS.blue];
      if (e.after === "suspendu") return ["SUSPENDU", HISTORY_COLORS.orange];
      if (e.after === "annule") return ["ANNULÉ", HISTORY_COLORS.red];
      if (e.after === "reactivated") return ["RÉACTIVÉ", HISTORY_COLORS.green];
      if (e.before === "cloture" && e.after === "en_cours") return ["RÉOUVERT", HISTORY_COLORS.red];
      if (e.after === "termine") return ["À VÉRIFIER", HISTORY_COLORS.violet];
      return ["STATUT", HISTORY_COLORS.blue];
    }
    if (e.event_type === "corriger") return ["À CORRIGER", "#E0822E"];
    if (e.event_type === "step_file_archived") return ["ARCHIVÉ", "#E0822E"];
    if (e.event_type === "step_close") return ["VALIDÉE", HISTORY_COLORS.green];
    if (e.event_type === "created") return ["CRÉÉ", HISTORY_COLORS.green];
    if (e.event_type === "step_removed" || e.event_type === "step_removed_manual") return ["SUPPRIMÉ", HISTORY_COLORS.red];
    if (e.event_type === "step_added") return ["AJOUTÉ", HISTORY_COLORS.green];
    const isFileField = e.field_key === "document" || e.field_type === "file";
    if (e.event_type === "field_change" && isFileField) {
      const hadBefore = e.before && String(e.before).trim() !== "";
      const hasAfter = e.after && String(e.after).trim() !== "";
      if (!hadBefore && hasAfter) return ["AJOUTÉ", HISTORY_COLORS.green];
      if (hadBefore && !hasAfter) return ["RETIRÉ", HISTORY_COLORS.red];
    }
    return [meta.badge || "MODIFIÉ", HISTORY_COLORS.amber];
  };
  const _statusLabel = (v) => ({ en_cours: "En cours", terminee: "Terminée", termine: "À vérifier", cloture: "Clôturé", suspendu: "Suspendu", annule: "Annulé", reactivated: "Réactivé", ouvert: "Ouvert", a_corriger: "À corriger" }[v] || v || "—");
  const timelineEvents = [
    ...(items.some((e) => e.event_type === "created") || !doc?.created_at ? [] : [{
      id: "created", at: doc.created_at, user: usersById[doc.created_by] || null,
      badge: "CRÉÉ", color: HISTORY_COLORS.green, detail: `Document ${doc.reference || ""} créé`.trim(),
    }]),
    ...items.map((e) => {
      const meta = EVENT_META[e.event_type] || EVENT_META.field_change;
      const [badge, color] = _badgeFor(e, meta);
      const actor = usersById[e.actor_id];
      const fileRef = _fileFromHistoryEvent(e);
      const isFileField = e.field_key === "document" || e.field_type === "file";
      let detail = _humanTitleForEvent(e, meta);
      if (e.event_type === "status_change") detail = `Statut : ${_statusLabel(e.before)} → ${_statusLabel(e.after)}`;
      if (e.event_type === "step_file_archived") detail = `Conservé pour traçabilité — remplacé suite à la correction demandée : ${e.note || "—"}`;
      if (e.event_type === "corriger") {
        const stepLabel = e.field_label || null;
        const isPiece = String(e.field_key || "").startsWith("piece[");
        const isDossier = (doc?.workflow_mode || "") === "dossier";
        detail = isPiece ? `Pièce « ${stepLabel || "Pièce"} » à corriger` : stepLabel ? `Correction demandée — étape « ${stepLabel} » réinitialisée (date et document retirés)` : isDossier ? "Dossier repassé « À corriger »" : "Document renvoyé « À refaire »";
      }
      let sub = null;
      if (fileRef && fileRef.url) {
        sub = (
          <FileChipsGroup files={[{ filename: fileRef.filename, url: fileRef.url }]}
            onView={(f) => openAuthenticatedUrl(f.url, { filename: f.filename })}
            onDownload={(f) => buildDownloadHandler(f.url, { filename: f.filename })()}
            testid={`history-file-${e.id}`} />
        );
      } else if (e.event_type !== "status_change" && (e.before !== undefined || e.after !== undefined) && !isFileField && (e.before || e.after)) {
        sub = <>{_formatValueForHistory(e.before)} → {_formatValueForHistory(e.after)}</>;
      }
      const note = e.note ? (String(e.note).match(/Motif(?:\s+d'annulation)?\s*:\s*(.+)$/i)?.[1] || e.note) : null;
      if (note) sub = <>{sub}{sub ? " · " : ""}<span className="italic">{e.event_type === "corriger" || /Motif/i.test(String(e.note)) ? `Motif : ${note}` : note}</span></>;
      return { id: e.id, at: e.created_at, user: actor || null, userName: e.actor_name, badge, color, detail, sub };
    }),
  ];

  return (
    <>
    {previousFilesSection}
    <UnifiedHistoryPanel entityType="document" entityId={docId} entityLabel="le document" height={560} testId="doc-history-panel" />
    </>
  );
}

// LinkedTasksCard removed 2026-02-16 — the Doc↔Task bidirectional link
// feature (Phase 4) has been fully dropped from Le Desk.

export const _reserved = null;
